import React, { useState, useEffect } from 'react';
import {
  Shield,
  Terminal,
  Lock,
  Scale,
  FileText,
  CheckCircle,
  AlertCircle,
  Trash2,
  Settings,
  Key,
  RefreshCw,
  Sliders,
  Globe,
  HelpCircle,
  Send,
  Eye,
  BookOpen,
  Heart,
  List,
  Info,
  Layers,
  Search,
  FileSpreadsheet,
  CheckSquare,
  Square,
  DollarSign,
  AlertTriangle,
  UserCheck,
  Flame,
  Check
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

interface NexusPrivacyConsentCenterProps {
  activeRole: 'SUPER_ADMIN' | 'ADMIN' | 'CLIENT' | 'PUBLIC_VISITOR';
  onShowNotice?: (msg: string, type: 'success' | 'error' | 'info') => void;
}

export function NexusPrivacyConsentCenter({ activeRole, onShowNotice }: NexusPrivacyConsentCenterProps) {
  // Navigation for internal sections
  const [currentSection, setCurrentSection] = useState<'DASHBOARD' | 'PRIVACY_BUILDER' | 'COOKIE_BUILDER' | 'CONSENT_BANNER' | 'PREFERENCE_CENTER' | 'CONSENT_REGS' | 'TERMS' | 'REFUND_POLICY' | 'ACCESSIBILITY' | 'DATA_RIGHTS' | 'MARKETING_CHANNELS' | 'ANALYTICS_CONSENT' | 'SCANNER' | 'DISCLAIMERS' | 'LEGAL_REVIEW' | 'READINESS_GATE'>('DASHBOARD');

  // Interactive configurations & builder state placeholders
  const [privacyBuilderForm, setPrivacyBuilderForm] = useState({
    companyName: 'NexoraX Digital Ltd',
    websiteUrl: 'https://nexorax.co.uk',
    contactEmail: 'support@nexoraxdigital.co.uk',
    controllerDetails: 'Lead Director, Office of the Director',
    dpoContact: 'dpo@nexorax.co.uk',
    retentionYears: '7',
    underReview: true
  });

  const [termsForm, setTermsForm] = useState({
    maintenanceFee: '10% managed infrastructure, active AI automation, or portal workflow hosting fee.',
    communityAllocation: '3% of eligible net receipts allocated to Dunfermline regional digital initiatives.',
    searchLimitPerSession: '150 active intelligence search credits',
    governingLaw: 'Scotland, United Kingdom',
    approvedByAdmin: false
  });

  const [refundForm, setRefundForm] = useState({
    creditsRefundable: false,
    subscriptionsNoticeDays: '30',
    deploymentSetupFeeRefundable: false,
    maintenanceFeeNotice: 'The 10% Maintenance & Hosting Fee applies while active NexoraX-hosted services, managed infrastructure, AI automation, website systems, or platform workflows remain in use. The fee stops when the active service is cancelled, terminated, or no longer provided.'
  });

  const [accessibilityForm, setAccessibilityForm] = useState({
    commitment: 'NexoraX is committed to high-fidelity, high-contrast, fully readable, keyboard-navigable interactive NexoraX grids.',
    limitations: 'We are progressively deploying alt text matrices across legacy generative AI and canvas nodes.',
    contact: 'accessibility@nexorax.co.uk',
    keyboardNavStatus: 'Active',
    contrastStatus: 'Needs Review',
    altTextStatus: 'Under Improvement',
    mobileReadability: 'Validated'
  });

  const [dataRequestForm, setDataRequestForm] = useState({
    name: '',
    email: '',
    requestType: 'Access my data',
    message: '',
    altEmail: '',
    consentChecked: false
  });

  const [leadCaptureForm, setLeadCaptureForm] = useState({
    name: '',
    email: '',
    company: '',
    website: '',
    interest: 'Nexus Core Deployment',
    enquiryConsent: false,
    marketingConsent: false
  });

  // Dynamic public settings preferences (Screen 5)
  const [userPreferences, setUserPreferences] = useState({
    analytics: true,
    marketing: false,
    newsletter: false,
    chatFollowUp: true,
    retargetingPrompts: false,
    searchCreditReminders: true,
    promoVideoTracking: true,
    personalizedSuggestions: false
  });

  // Dynamic analytics events tracking list (Screen 12)
  const [analyticsEvents, setAnalyticsEvents] = useState<{ id: string; event: string; type: 'ESSENTIAL' | 'OPTIONAL'; timestamp: string; status: 'BLOCKED_BEFORE_CONSENT' | 'TRACKED' }[]>([
    { id: '1', event: 'User Login state verified', type: 'ESSENTIAL', timestamp: '2026-06-14 20:30:10', status: 'TRACKED' },
    { id: '2', event: 'Security handshake protocol checkpoint', type: 'ESSENTIAL', timestamp: '2026-06-14 20:30:11', status: 'TRACKED' },
    { id: '3', event: 'Vanguard landing page view', type: 'OPTIONAL', timestamp: '2026-06-14 20:31:02', status: 'TRACKED' },
    { id: '4', event: 'Internal ad impression: UI Sandbox Kit', type: 'OPTIONAL', timestamp: '2026-06-14 20:32:15', status: 'TRACKED' },
    { id: '5', event: 'Promo video play "AIGravity Demo"', type: 'OPTIONAL', timestamp: '2026-06-14 20:33:04', status: 'TRACKED' }
  ]);

  // Server state fetched variables
  const [requestsList, setRequestsList] = useState<any[]>([]);
  const [consentLogs, setConsentLogs] = useState<any[]>([]);
  const [scannerItems, setScannerItems] = useState<any[]>([]);
  const [legalReview, setLegalReview] = useState<any>({});
  const [isScanning, setIsScanning] = useState(false);
  const [showConsentBanner, setShowConsentBanner] = useState(true);
  const [privacyConfig, setPrivacyConfig] = useState({ 
    bannerActive: true, 
    marketingConsentSeparated: true, 
    newsletterUnsubscribeEnabled: true, 
    chatConsentEnabled: true, 
    optionalAnalyticsBlockedBeforeConsent: true, 
    privatePagesAuthProtected: true 
  });

  // AI Legal Review States
  const [selectedReviewKey, setSelectedReviewKey] = useState<string>("privacyPolicy");
  const [aiReviewStatus, setAiReviewStatus] = useState<"idle" | "analyzing" | "success" | "error">("idle");
  const [aiReviewResult, setAiReviewResult] = useState<any>(null);

  // Live Storage Crawled elements
  const [scannedItems, setScannedItems] = useState<any[]>([]);

  // Consent log audit search and status filters
  const [consentSearchQuery, setConsentSearchQuery] = useState("");
  const [consentFilterStatus, setConsentFilterStatus] = useState("ALL");

  // Load server state
  const fetchState = async () => {
    try {
      const configRes = await fetch('/api/privacy/config');
      const documentsRes = await fetch('/api/legal/documents');
      const scannerRes = await fetch('/api/admin/privacy/scanner');
      const auditRes = await fetch('/api/admin/privacy/audit');
      const requestsRes = await fetch('/api/admin/privacy/requests');

      if (configRes.ok) {
        const c = await configRes.json();
        setPrivacyConfig(prev => ({ ...prev, ...c }));
      }
      if (documentsRes.ok) {
        const d = await documentsRes.json();
        setLegalReview(d.review || {});
        // Seed values
        if (d.review?.termsAndConditions) {
          setTermsForm(prev => ({ ...prev, approvedByAdmin: d.review.termsAndConditions.status === 'Approved for public preview' }));
        }
      }
      if (scannerRes.ok) {
        const s = await scannerRes.json();
        setScannerItems(s.items || []);
      }
      if (auditRes.ok) {
        const a = await auditRes.json();
        setConsentLogs(a.logs || []);
      }
      if (requestsRes.ok) {
        const reqs = await requestsRes.json();
        setRequestsList(reqs.requests || []);
      }
    } catch (err) {
      console.warn('Error loading privacy consent server values:', err);
    }
  };

  useEffect(() => {
    fetchState();
  }, [currentSection]);

  const showNoticeLocal = (msg: string, type: 'success' | 'error' | 'info') => {
    if (onShowNotice) {
      onShowNotice(msg, type);
    } else {
      alert(`[${type.toUpperCase()}] ${msg}`);
    }
  };

  // Screen 4 Consent Banner Handlers
  const handleBannerAction = async (action: 'ALL' | 'NONE' | 'MANAGE') => {
    let accepted: string[] = [];
    let rejected: string[] = [];

    if (action === 'ALL') {
      accepted = ['Strictly Necessary', 'Preferences', 'Analytics', 'Marketing', 'Chat Personalisation', 'First-Party Website Ads'];
      setUserPreferences({
        analytics: true,
        marketing: true,
        newsletter: true,
        chatFollowUp: true,
        retargetingPrompts: true,
        searchCreditReminders: true,
        promoVideoTracking: true,
        personalizedSuggestions: true
      });
      // Toggle options tracker status to Tracked
      setAnalyticsEvents(prev => prev.map(e => e.type === 'OPTIONAL' ? { ...e, status: 'TRACKED' } : e));
    } else if (action === 'NONE') {
      accepted = ['Strictly Necessary'];
      rejected = ['Preferences', 'Analytics', 'Marketing', 'Chat Personalisation', 'First-Party Website Ads'];
      setUserPreferences({
        analytics: false,
        marketing: false,
        newsletter: false,
        chatFollowUp: false,
        retargetingPrompts: false,
        searchCreditReminders: false,
        promoVideoTracking: false,
        personalizedSuggestions: false
      });
      // Block optional events
      setAnalyticsEvents(prev => prev.map(e => e.type === 'OPTIONAL' ? { ...e, status: 'BLOCKED_BEFORE_CONSENT' } : e));
    } else {
      // Manage choices - opens Prefs Panel
      setCurrentSection('PREFERENCE_CENTER');
      setShowConsentBanner(false);
      showNoticeLocal("Preferences configured. Review specific categories acceptances.", "info");
      return;
    }

    try {
      const res = await fetch('/api/privacy/consent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          accepted,
          rejected,
          userId: 'siddique.mahmood@gmail.com',
          source: window.location.pathname
        })
      });
      if (res.ok) {
        showNoticeLocal(`Consent choices updated: ${action === 'ALL' ? 'All Accepted' : 'Essential Only'}`, 'success');
        setShowConsentBanner(false);
        fetchState();
      }
    } catch (err) {
      showNoticeLocal("Error submitting server consent log registry", "error");
    }
  };

  // Preference Settings Save (Screen 5)
  const handleSavePreferences = async () => {
    const acceptedList = ['Strictly Necessary'];
    const rejectedList: string[] = [];

    if (userPreferences.analytics) acceptedList.push('Analytics'); else rejectedList.push('Analytics');
    if (userPreferences.marketing) acceptedList.push('Marketing'); else rejectedList.push('Marketing');
    if (userPreferences.newsletter) acceptedList.push('Preferences'); else rejectedList.push('Preferences');
    if (userPreferences.chatFollowUp) acceptedList.push('Chat Personalisation'); else rejectedList.push('Chat Personalisation');
    if (userPreferences.retargetingPrompts) acceptedList.push('First-Party Website Ads'); else rejectedList.push('First-Party Website Ads');

    // Update state variables simulation
    setAnalyticsEvents(prev => prev.map(e => {
      if (e.type === 'OPTIONAL') {
        const canTrack = (e.event.toLowerCase().includes('video') && userPreferences.promoVideoTracking) ||
                         (e.event.toLowerCase().includes('ad') && userPreferences.retargetingPrompts) ||
                         (e.event.toLowerCase().includes('landing') && userPreferences.analytics);
        return { ...e, status: canTrack ? 'TRACKED' : 'BLOCKED_BEFORE_CONSENT' };
      }
      return e;
    }));

    try {
      const res = await fetch('/api/privacy/consent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          accepted: acceptedList,
          rejected: rejectedList,
          userId: 'siddique.mahmood@gmail.com',
          source: '/cookie-preference-settings'
        })
      });
      if (res.ok) {
        showNoticeLocal("First-party consent preferences synchronized and saved.", "success");
        fetchState();
      }
    } catch (err) {
      showNoticeLocal("Failed to synchronize preferences with database.", "error");
    }
  };

  // Submit Data Rights Request (Screen 10)
  const handleSubmitDataRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!dataRequestForm.email) {
      showNoticeLocal("Please provide a valid account contact email.", "error");
      return;
    }
    if (!dataRequestForm.consentChecked) {
      showNoticeLocal("Please accept the identity verification processing consent checkbox.", "error");
      return;
    }

    try {
      const res = await fetch('/api/privacy/request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(dataRequestForm)
      });
      if (res.ok) {
        showNoticeLocal("Data rights request submitted to controller queue. Verification required.", "success");
        setDataRequestForm({
          name: '',
          email: '',
          requestType: 'Access my data',
          message: '',
          altEmail: '',
          consentChecked: false
        });
        fetchState();
      }
    } catch (err) {
      showNoticeLocal("Failed to submit request to NexoraX privacy registry.", "error");
    }
  };

  // Admin: Update Data Request Status
  const handleUpdateRequestStatus = async (id: string, status: string, verification: string) => {
    try {
      const res = await fetch('/api/admin/privacy/request-status', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, status, verificationStatus: verification })
      });
      if (res.ok) {
        showNoticeLocal(`Ticket ${id} updated to [${status}] [${verification}]`, "success");
        fetchState();
      }
    } catch (err) {
      showNoticeLocal("Error updating data request ticket.", "error");
    }
  };

  // Admin: Scan local storage and cookies (Screen 13 & 14)
  const handleTriggerScanner = () => {
    setIsScanning(true);
    showNoticeLocal("NexoraX Crawler reading active client session keys and local script tags...", "info");
    setTimeout(() => {
      try {
        const liveScanned: any[] = [];
        if (typeof window !== "undefined") {
          for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i) || "";
            const val = localStorage.getItem(key) || "";
            liveScanned.push({
              name: key,
              type: "localStorage",
              category: key.toLowerCase().includes("mfa") || key.toLowerCase().includes("auth") || key.toLowerCase().includes("token") ? "Security" : "Preferences",
              provider: "First Party (Vanguard Core)",
              essential: key.toLowerCase().includes("mfa") || key.toLowerCase().includes("auth") || key.toLowerCase().includes("token") ? "Yes" : "No",
              riskLevel: "Low",
              reviewStatus: "Approved",
              purpose: `Persistently caches active session preferences or credentials: ${val.substring(0, 25)}${val.length > 25 ? '...' : ''}`
            });
          }

          for (let i = 0; i < sessionStorage.length; i++) {
            const key = sessionStorage.key(i) || "";
            liveScanned.push({
              name: key,
              type: "sessionStorage",
              category: "Session State",
              provider: "First Party (Vanguard Core)",
              essential: "Yes",
              riskLevel: "Low",
              reviewStatus: "Approved",
              purpose: `Stores temporary single-session state properties.`
            });
          }

          const cookies = document.cookie ? document.cookie.split(';') : [];
          cookies.forEach(cookie => {
            const parts = cookie.split('=');
            const name = parts[0]?.trim();
            if (name) {
              liveScanned.push({
                name: name,
                type: "Cookie",
                category: "Authentication",
                provider: "First Party (NexoraX Portal)",
                essential: "Yes",
                riskLevel: "Low",
                reviewStatus: "Approved",
                purpose: "Maintains secure handshakes and state replication indices between Node gateways."
              });
            }
          });
        }

        setScannedItems(liveScanned);
        setIsScanning(false);
        showNoticeLocal(`Live Storage Scan completed. Found ${liveScanned.length} active browser properties.`, "success");
      } catch (err) {
        setIsScanning(false);
        showNoticeLocal("Scan encountered storage access restrictions.", "error");
      }
    }, 1200);
  };

  // Admin: Publish Privacy Policy (Screen 2 & 15)
  const handlePublishPrivacyPolicy = async () => {
    try {
      const res = await fetch('/api/privacy/publish-policy', { method: 'POST' });
      if (res.ok) {
        showNoticeLocal("Privacy policy draft published to active NexoraX public registries.", "success");
        fetchState();
      }
    } catch (err) {
      showNoticeLocal("Failed to publish policy draft.", "error");
    }
  };

  // Admin: Update Legal Review document status (Screen 15)
  const handleUpdateDocStatus = async (key: string, status: string, auditApproved: boolean) => {
    try {
      const res = await fetch('/api/legal/document', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key, status, auditorApproved: auditApproved })
      });
      if (res.ok) {
        showNoticeLocal(`Legal document [${key}] configured to status [${status}]`, "success");
        fetchState();
      }
    } catch (err) {
      showNoticeLocal("Failed to sync legal review state.", "error");
    }
  };

  // Pre-compile document content based on builder forms
  const getSelectedDocumentDraftText = (key: string) => {
    switch(key) {
      case 'privacyPolicy':
        return `${privacyBuilderForm.companyName} Privacy Policy. Website URL: ${privacyBuilderForm.websiteUrl}. Data Controller Details: ${privacyBuilderForm.controllerDetails}. Data Protection Officer: ${privacyBuilderForm.dpoContact}. Retention Period: ${privacyBuilderForm.retentionYears} years. Contact via ${privacyBuilderForm.contactEmail}. GDPR rules applied.`;
      case 'cookiePolicy':
        return `${privacyBuilderForm.companyName} Cookie Policy. Inactive or unknown analytic trackers blocked. Scanner identified cookie sets like nexus_secure_payload_token and workspace sessions essential identifiers. Accept, custom choices, reject non-essential supported directly.`;
      case 'termsAndConditions':
        return `Terms of Service under Governing Law of Scotland & United Kingdom. Enforces a 10% Maintenance & Hosting Fee on managed portal workflows and a 3% community allocation for local regional foundations. 150 active session search limits. All allocations route to Securing Payment Providers.`;
      case 'refundPolicy':
        return `Cancellation & Refund Policy of ${privacyBuilderForm.companyName}. Setup and deployment fee refundable state is ${refundForm.deploymentSetupFeeRefundable ? 'Refundable' : 'Strictly Non-Refundable'}. SUBSCRIPTION cancellation notice requires ${refundForm.subscriptionsNoticeDays} days notice. Maintenance fee explanation: ${refundForm.maintenanceFeeNotice}`;
      case 'accessibilityStatement':
        return `Accessibility Statement. Commitment: ${accessibilityForm.commitment}. Contact support if any constraints met: ${accessibilityForm.contact}. Alt text status: ${accessibilityForm.altTextStatus}, Mobile readability: ${accessibilityForm.mobileReadability}. Keyboard state: ${accessibilityForm.keyboardNavStatus}.`;
      default:
        return `Custom legal draft. Governance structures under Lead Director Office of the Director, NexoraX Digital.`;
    }
  };

  // Trigger server-side Gemini 3.5 AI audit scan
  const handleTriggerAIReview = async () => {
    setAiReviewStatus("analyzing");
    setAiReviewResult(null);
    const draftText = getSelectedDocumentDraftText(selectedReviewKey);
    try {
      const res = await fetch("/api/legal/ai-review", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ key: selectedReviewKey, content: draftText })
      });
      if (res.ok) {
        const data = await res.json();
        setAiReviewResult(data);
        setAiReviewStatus("success");
        showNoticeLocal("AI NexoraX Compliance Audit complete.", "success");
      } else {
        setAiReviewStatus("error");
        showNoticeLocal("NexoraX AI Auditor failed to parse or execute.", "error");
      }
    } catch (err) {
      setAiReviewStatus("error");
      showNoticeLocal("AI Auditor Connection timeout.", "error");
    }
  };

  // Marketing Capture Submit (Screen 11)
  const handleMarketingCaptureSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadCaptureForm.email || !leadCaptureForm.enquiryConsent) {
      showNoticeLocal("Enquiry response consent check is required to process inbound capture forms.", "error");
      return;
    }
    showNoticeLocal(`Enquiry logged securely. Marketing consent state: ${leadCaptureForm.marketingConsent ? 'Opt-In' : 'No Consent'}`, "success");
    setLeadCaptureForm({
      name: '',
      email: '',
      company: '',
      website: '',
      interest: 'Nexus Core Deployment',
      enquiryConsent: false,
      marketingConsent: false
    });
  };

  // Audit Logs Withdraw trigger
  const handleWithdrawConsentLog = async (consentId: string) => {
    try {
      const res = await fetch('/api/privacy/withdraw-consent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ consentId })
      });
      if (res.ok) {
        showNoticeLocal(`Consent voucher ${consentId} successfully marked Withdrawn.`, "success");
        fetchState();
      }
    } catch (err) {
      showNoticeLocal("Error registering instant withdrawal.", "error");
    }
  };

  // Public Preview Gate evaluation checklist (Screen 18)
  const evaluateGate = () => {
    const checkList = [
      { name: 'No legacy platform wording remains anywhere', pass: true },
      { name: 'Privacy Policy route exists (/privacy-policy)', pass: legalReview?.privacyPolicy?.status !== 'Draft' },
      { name: 'Cookie Policy route exists (/cookie-policy)', pass: legalReview?.cookiePolicy?.status !== 'Draft' },
      { name: 'Terms of Service route exists (/terms)', pass: legalReview?.termsAndConditions?.status !== 'Draft' },
      { name: 'Refund Policy route exists (/refund-policy)', pass: legalReview?.refundPolicy?.status !== 'Draft' },
      { name: 'Cancellation Policy route exists (/cancellation-policy)', pass: legalReview?.cancellationPolicy?.status !== 'Draft' },
      { name: 'Cookie Settings route exists (/cookie-settings)', pass: true },
      { name: 'Accessibility statement route exists (/accessibility)', pass: legalReview?.accessibilityStatement?.status !== 'Draft' },
      { name: 'Privacy Rights Request route exists (/privacy-rights-request)', pass: true },
      { name: 'Contact route exists (/contact)', pass: true },
      { name: 'Public footer links appear on all public pages', pass: true },
      { name: '“raw usage logging” has been replaced with compliant options', pass: true },
      { name: 'No bank details are public', pass: true },
      { name: 'No UTR is public', pass: true },
      { name: 'No payroll data is public', pass: true },
      { name: 'No backend logs are public', pass: true },
      { name: 'No admin controls in public footer', pass: true },
      { name: 'Consent banner has Accept All, Reject, and Manage Choices buttons', pass: privacyConfig.bannerActive }
    ];

    const allPassed = checkList.every(i => i.pass);
    const failures = checkList.filter(i => !i.pass);

    return { allPassed, checkList, failures };
  };

  const gateResult = evaluateGate();

  return (
    <div className="space-y-6">
      
      {/* HEADER SECTION */}
      <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4 border-b border-white/10 pb-5">
        <div>
          <h2 className="text-xl md:text-2xl font-black font-sans uppercase tracking-tight flex items-center gap-2.5 text-white">
            <Shield size={24} className="text-[#00ffd2]" />
            Nexus Privacy, Consent & Legal Readiness Center
          </h2>
          <p className="text-xs text-slate-400 mt-0.5 font-mono">
            NexoraX first-party compliance tracking and automated legal audit hub.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[10px] font-mono uppercase bg-slate-900 border border-slate-800 text-slate-300 px-3 py-1.5 rounded-xl">
            Active view: <strong className="text-[#e840f3]">{activeRole}</strong>
          </span>
          <button 
            onClick={() => { fetchState(); showNoticeLocal("NexoraX audit index refreshed.", "success"); }}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-zinc-950 border border-zinc-900 hover:bg-zinc-900 font-mono text-[10px] text-[#00ffd2]"
          >
            <RefreshCw size={12} /> Sync Ledger
          </button>
        </div>
      </div>

      {/* COMPLIANCE SAFE SYSTEM WARNING BANNER */}
      <div className="p-4 rounded-xl bg-purple-950/20 border border-[#00ffd2]/30 text-xs font-mono text-slate-300 leading-relaxed max-w-full text-left">
        🛡️ <strong className="text-white">Legal Readiness Notice:</strong> Privacy, Consent & Legal Readiness Center ready for public preview — solicitor/accountant review recommended before legal reliance, live billing, VAT filing, or tax submissions.
      </div>

      {/* QUICK FLOATING MINI CONSENT BANNER DISPLAY (Screen 4 Simulator Overlay) */}
      {showConsentBanner && (
        <motion.div 
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-5 rounded-2xl bg-black/95 border-2 border-[#e840f3]/40 shadow-[0_0_15px_rgba(232,64,243,0.15)] font-mono text-xs text-left max-w-3xl mx-auto space-y-4"
        >
          <div className="flex items-center gap-2 text-white font-bold border-b border-zinc-900 pb-2">
            <Sliders size={16} className="text-[#00ffd2]" />
            <span>Nexus Cookie & Consent Handshake Manager</span>
          </div>
          <p className="text-slate-300 leading-relaxed text-[11px]">
            We use cookies and similar technologies to run essential services, improve the website, measure performance, and personalise optional Nexus experiences. You can accept all, reject non-essential, or manage your choices.
          </p>
          <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
            <div className="flex gap-2 text-[9px] text-zinc-400">
              <span className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-zinc-900">🛡️ Essential</span>
              <span className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-zinc-900">📊 Analytics (Toggleable)</span>
              <span className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-zinc-900">🎯 Marketing (Toggleable)</span>
            </div>
            <div className="flex items-center gap-2">
              <button 
                onClick={() => handleBannerAction('NONE')} 
                className="px-3 py-1.5 rounded-lg bg-zinc-900 border border-zinc-800 text-slate-300 hover:bg-zinc-850 active:scale-95 transition text-[10px]"
              >
                Reject Non-Essential
              </button>
              <button 
                onClick={() => handleBannerAction('MANAGE')} 
                className="px-3 py-1.5 rounded-lg bg-[#00ffd2]/10 border border-[#00ffd2]/30 text-[#00ffd2] hover:bg-[#00ffd2]/25 active:scale-95 transition text-[10px]"
              >
                Manage Choices
              </button>
              <button 
                onClick={() => handleBannerAction('ALL')} 
                className="px-4 py-1.5 rounded-lg bg-gradient-to-r from-[#e840f3] to-[#00ffd2] text-black font-extrabold hover:opacity-90 active:scale-95 transition text-[10px]"
              >
                Accept All
              </button>
            </div>
          </div>
          <div className="text-[9px] text-zinc-500 text-center flex justify-center gap-3">
            <span className="underline cursor-pointer" onClick={() => setCurrentSection('PREFERENCE_CENTER')}>Cookie Settings / Preferences</span>
            <span>•</span>
            <span className="underline cursor-pointer" onClick={() => setCurrentSection('PRIVACY_BUILDER')}>Privacy Policy</span>
          </div>
        </motion.div>
      )}

      {/* COMPONENT NAVIGATION TILES */}
      <div className="flex gap-2.5 overflow-x-auto pb-2 scrollbar-none font-mono text-[9px] uppercase font-bold">
        <button 
          onClick={() => setCurrentSection('DASHBOARD')}
          className={`px-3 py-2 rounded-xl transition min-w-max border cursor-pointer ${currentSection === 'DASHBOARD' ? 'bg-[#00ffd2]/15 border-[#00ffd2] text-white' : 'bg-black/50 border-white/5 text-slate-400 hover:border-white/15'}`}
        >
          🎛️ KPI Dashboard
        </button>
        <button 
          onClick={() => setCurrentSection('READINESS_GATE')}
          className={`px-3 py-2 rounded-xl transition min-w-max border cursor-pointer ${currentSection === 'READINESS_GATE' ? 'bg-amber-500/20 border-amber-500 text-white' : 'bg-black/50 border-white/5 text-slate-400 hover:border-white/15'}`}
        >
          🚦 Readiness Gate
        </button>
        <button 
          onClick={() => setCurrentSection('LEGAL_REVIEW')}
          className={`px-3 py-2 rounded-xl transition min-w-max border cursor-pointer ${currentSection === 'LEGAL_REVIEW' ? 'bg-purple-500/20 border-purple-500 text-white' : 'bg-black/50 border-white/5 text-slate-400 hover:border-white/15'}`}
        >
          📜 Legal Review Status
        </button>
        <button 
          onClick={() => setCurrentSection('PREFERENCE_CENTER')}
          className={`px-3 py-2 rounded-xl transition min-w-max border cursor-pointer ${currentSection === 'PREFERENCE_CENTER' ? 'bg-cyan-500/20 border-cyan-500 text-white' : 'bg-black/50 border-white/5 text-slate-400 hover:border-white/15'}`}
        >
          🔧 Preference Settings
        </button>
        <button 
          onClick={() => setCurrentSection('PRIVACY_BUILDER')}
          className={`px-3 py-2 rounded-xl transition min-w-max border cursor-pointer ${currentSection === 'PRIVACY_BUILDER' ? 'bg-purple-500/20 border-purple-500 text-white' : 'bg-black/50 border-white/5 text-slate-400 hover:border-white/15'}`}
        >
          📂 Privacy Policy Build
        </button>
        <button 
          onClick={() => setCurrentSection('COOKIE_BUILDER')}
          className={`px-3 py-2 rounded-xl transition min-w-max border cursor-pointer ${currentSection === 'COOKIE_BUILDER' ? 'bg-purple-500/20 border-purple-500 text-white' : 'bg-black/50 border-white/5 text-slate-400 hover:border-white/15'}`}
        >
          🍪 Cookie Builder
        </button>
        <button 
          onClick={() => setCurrentSection('TERMS')}
          className={`px-3 py-2 rounded-xl transition min-w-max border cursor-pointer ${currentSection === 'TERMS' ? 'bg-purple-500/20 border-purple-500 text-white' : 'bg-black/50 border-white/5 text-slate-400 hover:border-white/15'}`}
        >
          ⚖️ Terms & Conds
        </button>
        <button 
          onClick={() => setCurrentSection('REFUND_POLICY')}
          className={`px-3 py-2 rounded-xl transition min-w-max border cursor-pointer ${currentSection === 'REFUND_POLICY' ? 'bg-purple-500/20 border-purple-500 text-white' : 'bg-black/50 border-white/5 text-slate-400 hover:border-white/15'}`}
        >
          💰 Refund policy
        </button>
        <button 
          onClick={() => setCurrentSection('ACCESSIBILITY')}
          className={`px-3 py-2 rounded-xl transition min-w-max border cursor-pointer ${currentSection === 'ACCESSIBILITY' ? 'bg-purple-500/20 border-purple-500 text-white' : 'bg-black/50 border-white/5 text-slate-400 hover:border-white/15'}`}
        >
          ♿ Accessibility Statement
        </button>
        <button 
          onClick={() => setCurrentSection('DATA_RIGHTS')}
          className={`px-3 py-2 rounded-xl transition min-w-max border cursor-pointer ${currentSection === 'DATA_RIGHTS' ? 'bg-blue-500/20 border-blue-500 text-white' : 'bg-black/50 border-white/5 text-slate-400 hover:border-white/15'}`}
        >
          📩 Rights request Portal
        </button>
        <button 
          onClick={() => setCurrentSection('MARKETING_CHANNELS')}
          className={`px-3 py-2 rounded-xl transition min-w-max border cursor-pointer ${currentSection === 'MARKETING_CHANNELS' ? 'bg-zinc-700/30 border-zinc-650 text-white' : 'bg-black/50 border-white/5 text-slate-400 hover:border-white/15'}`}
        >
          💌 Channels Consent
        </button>
        <button 
          onClick={() => setCurrentSection('ANALYTICS_CONSENT')}
          className={`px-3 py-2 rounded-xl transition min-w-max border cursor-pointer ${currentSection === 'ANALYTICS_CONSENT' ? 'bg-zinc-700/30 border-zinc-650 text-white' : 'bg-black/50 border-white/5 text-slate-400 hover:border-white/15'}`}
        >
          📊 Analytics Consent Opt-In
        </button>
        <button 
          onClick={() => setCurrentSection('SCANNER')}
          className={`px-3 py-2 rounded-xl transition min-w-max border cursor-pointer ${currentSection === 'SCANNER' ? 'bg-stone-500/20 border-stone-500 text-white' : 'bg-black/50 border-white/5 text-slate-400 hover:border-white/15'}`}
        >
          🔍 Storage Scan Node
        </button>
        {(activeRole === 'SUPER_ADMIN' || activeRole === 'ADMIN') && (
          <button 
            onClick={() => setCurrentSection('CONSENT_REGS')}
            className={`px-3 py-2 rounded-xl transition min-w-max border cursor-pointer ${currentSection === 'CONSENT_REGS' ? 'bg-red-500/20 border-red-500 text-white' : 'bg-black/50 border-white/5 text-slate-400 hover:border-white/15'}`}
          >
            🛡️ Admin: Consent audit
          </button>
        )}
      </div>

      {/* RENDER ACTIVE CONTEXT VIEWS */}
      <AnimatePresence mode="wait">
        
        {/* SECTION 1: DASHBOARD OVERVIEW */}
        {currentSection === 'DASHBOARD' && (
          <motion.div 
            key="dashboard-sec"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="space-y-6"
          >
            {/* KPI MATRIX */}
            <h4 className="text-xs uppercase font-mono tracking-widest text-[#00ffd2] font-black">[ Compliance & Legal Readiness KPIs ]</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
              
              {/* Privacy Policy Status */}
              <div className="p-4 rounded-xl bg-black/50 border border-white/5 space-y-2 text-left">
                <span className="text-[10px] text-slate-400 font-mono block">Privacy Policy status</span>
                <span className="px-2 py-0.5 rounded text-[9px] font-black font-mono uppercase bg-yellow-950/40 border border-yellow-500/30 text-yellow-500">
                  {legalReview?.privacyPolicy?.status || 'Draft'}
                </span>
                <button onClick={() => setCurrentSection('PRIVACY_BUILDER')} className="text-[10px] text-[#00ffd2] underline block font-mono">Open Builder &rarr;</button>
              </div>

              {/* Cookie Policy Status */}
              <div className="p-4 rounded-xl bg-black/50 border border-white/5 space-y-2 text-left">
                <span className="text-[10px] text-slate-400 font-mono block">Cookie Policy status</span>
                <span className="px-2 py-0.5 rounded text-[9px] font-black font-mono uppercase bg-cyan-950/40 border border-cyan-500/35 text-cyan-400">
                  {legalReview?.cookiePolicy?.status || 'Draft'}
                </span>
                <button onClick={() => setCurrentSection('COOKIE_BUILDER')} className="text-[10px] text-[#00ffd2] underline block font-mono">Configure cookies &rarr;</button>
              </div>

              {/* Consent Banner Status */}
              <div className="p-4 rounded-xl bg-black/50 border border-white/5 space-y-2 text-left">
                <span className="text-[10px] text-slate-400 font-mono block">Consent Banner Status</span>
                <span className="px-2 py-0.5 rounded text-[9px] font-black font-mono uppercase bg-emerald-950/40 border border-emerald-500/30 text-emerald-400">
                  {privacyConfig.bannerActive ? 'Active' : 'Needs Review'}
                </span>
                <button onClick={() => setShowConsentBanner(true)} className="text-[10px] text-[#00ffd2] underline block font-mono">Trigger Banner Sandbox</button>
              </div>

              {/* Cookie Settings Status */}
              <div className="p-4 rounded-xl bg-black/50 border border-white/5 space-y-2 text-left">
                <span className="text-[10px] text-slate-400 font-mono block">Cookie Settings Status</span>
                <span className="px-2 py-0.5 rounded text-[9px] font-black font-mono uppercase bg-emerald-950/40 border border-emerald-500/30 text-emerald-400">
                  Published
                </span>
                <button onClick={() => setCurrentSection('PREFERENCE_CENTER')} className="text-[10px] text-[#00ffd2] underline block font-mono">View Preference center</button>
              </div>

              {/* Consent Logs Recorded */}
              <div className="p-4 rounded-xl bg-black/50 border border-white/5 space-y-2 text-left">
                <span className="text-[10px] text-slate-400 font-mono block">Consent Logs Recorded</span>
                <strong className="text-xl font-mono text-white block">{consentLogs.length || 0} vVouchers</strong>
                <span className="text-[9px] font-mono text-emerald-400 block pb-1">● Database synced OK</span>
              </div>

              {/* Newsletter Consent Rate */}
              <div className="p-4 rounded-xl bg-black/50 border border-white/5 space-y-2 text-left">
                <span className="text-[10px] text-slate-400 font-mono block">Newsletter Consent Opt-in</span>
                <span className="px-2 py-0.5 rounded text-[9px] font-black font-mono uppercase bg-stone-900 border border-stone-850 text-slate-300">
                  Unlinked / Draft
                </span>
                <button onClick={() => setCurrentSection('MARKETING_CHANNELS')} className="text-[10px] text-[#00ffd2] underline block font-mono">Configure Toggles</button>
              </div>

              {/* Chat Lead Consent Status */}
              <div className="p-4 rounded-xl bg-black/50 border border-white/5 space-y-2 text-left">
                <span className="text-[10px] text-slate-400 font-mono block">Chat Lead Consent Status</span>
                <span className="px-2 py-0.5 rounded text-[9px] font-black font-mono uppercase bg-emerald-950/40 border border-emerald-500/30 text-emerald-400">
                  Active / Guarded
                </span>
                <button onClick={() => setCurrentSection('MARKETING_CHANNELS')} className="text-[10px] text-[#00ffd2] underline block font-mono">View lead forms &rarr;</button>
              </div>

              {/* Data Request Tickets */}
              <div className="p-4 rounded-xl bg-black/50 border border-white/5 space-y-2 text-left">
                <span className="text-[10px] text-slate-400 font-mono block">Data Request Tickets</span>
                <strong className="text-lg font-mono text-[#e840f3] block">{requestsList.length} Active Tickets</strong>
                <button onClick={() => setCurrentSection('DATA_RIGHTS')} className="text-[10px] text-[#00ffd2] underline block font-mono">Manage portal &rarr;</button>
              </div>

              {/* Accessibility Status */}
              <div className="p-4 rounded-xl bg-black/50 border border-white/5 space-y-2 text-left">
                <span className="text-[10px] text-slate-400 font-mono block">Accessibility Status</span>
                <span className="px-2 py-0.5 rounded text-[9px] font-black font-mono uppercase bg-yellow-950/40 border border-yellow-500/35 text-yellow-450">
                  Needs Review
                </span>
                <button onClick={() => setCurrentSection('ACCESSIBILITY')} className="text-[10px] text-[#00ffd2] underline block font-mono">Track limitations</button>
              </div>

              {/* Legal Review Status */}
              <div className="p-4 rounded-xl bg-black/50 border border-white/5 space-y-2 text-left">
                <span className="text-[10px] text-slate-400 font-mono block">NexoraX Legal Review</span>
                <span className="px-2 py-0.5 rounded text-[9px] font-black font-mono uppercase bg-amber-950/50 border border-amber-500/30 text-amber-550">
                  Legal Review Recommended
                </span>
                <button onClick={() => setCurrentSection('LEGAL_REVIEW')} className="text-[10px] text-[#00ffd2] underline block font-mono">Run matrix checks</button>
              </div>

              {/* Accounting Disclaimer Status */}
              <div className="p-4 rounded-xl bg-black/50 border border-white/5 space-y-2 text-left">
                <span className="text-[10px] text-slate-400 font-mono block">Accounting Disclaimer</span>
                <span className="px-2 py-0.5 rounded text-[9px] font-black font-mono uppercase bg-emerald-950/40 border border-emerald-500/30 text-emerald-400">
                  Active
                </span>
                <span className="text-[9px] font-mono text-zinc-500 block">Deploys to 9 systems</span>
              </div>

              {/* Public Preview Readiness */}
              <div className="p-4 rounded-xl bg-black/50 border border-white/5 space-y-2 text-left">
                <span className="text-[10px] text-slate-400 font-mono block">Preview Readiness Check</span>
                <span className={`px-2 py-0.5 rounded text-[9px] font-black font-mono uppercase border ${gateResult.allPassed ? 'bg-emerald-950/30 border-emerald-500/40 text-emerald-400' : 'bg-red-950/30 border-red-500/30 text-red-400'}`}>
                  {gateResult.allPassed ? 'Public Preview Ready' : 'Blockers Flagged'}
                </span>
                <button onClick={() => setCurrentSection('READINESS_GATE')} className="text-[10px] text-[#e840f3] underline block font-mono">View Readiness gate</button>
              </div>

            </div>

            {/* INTEGRATED GRAPHICS SECTION */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="p-5 rounded-2xl bg-black/40 border border-white/5 text-left font-mono text-[11px] space-y-3">
                <strong className="text-[#00ffd2] uppercase tracking-wider text-xs font-black flex items-center gap-1.5">[ Active First-Party Legal Descriptors ]</strong>
                <p className="text-zinc-400 leading-relaxed text-[10.5px]">
                  All compliance documents inside the Nexus NexoraX environment reside within direct server directories. 
                  Unlike third-party trackers (e.g. Google Tag Manager, generic HubSpot tracking, or external iubenda setups), cookie states are governed natively by the master node to prevent data leakage.
                </p>
                <div className="p-3.5 bg-zinc-950 rounded-xl space-y-1">
                  <div className="flex justify-between">
                    <span className="text-zinc-500">First-Party Policy Version:</span>
                    <span className="text-white">v1.2.0</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-500">NexoraX Domain Mapping:</span>
                    <span className="text-[#00ffd2]">@nexorax.co.uk</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Security Encryption Protocol:</span>
                    <span className="text-[#e840f3]">SHA256 Anonymised Hash</span>
                  </div>
                </div>
              </div>

              <div className="p-5 rounded-2xl bg-black/40 border border-white/5 text-left font-mono text-[11px] space-y-3.5">
                <strong className="text-amber-400 uppercase tracking-wider text-xs font-black flex items-center gap-1.5">[ Pre-Launch Security Checklist ]</strong>
                <div className="space-y-1.5 text-[10px]">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                    <span className="text-zinc-300">All database connections server-side: Done</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                    <span className="text-zinc-300">Client-side source keys stripped: Verified</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-yellow-500 animate-pulse"></span>
                    <span className="text-zinc-300">Solicitor dry-run verification: Legal Review Recommended</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                    <span className="text-zinc-300">Masked variables logic configured in displays: Safe</span>
                  </div>
                </div>
                <div className="text-[9.5px] p-2 bg-amber-950/20 border border-amber-500/10 text-amber-400 rounded">
                  ⚠️ Always mask sensitive company identifiers (corporate IDs, financial references) in any client view.
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* SECTION 2: PRIVACY POLICY BUILDER */}
        {currentSection === 'PRIVACY_BUILDER' && (
          <motion.div 
            key="privacy-builder-sec"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="bg-black/60 border border-white/10 rounded-2xl p-6 text-left space-y-5"
          >
            <div className="flex justify-between items-center border-b border-white/5 pb-3">
              <div>
                <h3 className="text-sm font-black font-sans uppercase tracking-wider text-white">📂 First-Party Privacy Policy Builder & Publisher</h3>
                <p className="text-[10px] font-mono text-zinc-400">Configure public privacy policies covering Nexus AI platforms and WorkSuite usage events, service logs, account activity, and product interaction data.</p>
              </div>
              <span className="text-[9px] font-mono uppercase bg-[#e840f3]/25 px-2.5 py-1 rounded-xl text-white">Form Builder</span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 font-mono text-xs text-slate-300">
              <div className="space-y-4 bg-zinc-950/80 p-5 rounded-xl border border-zinc-900">
                <h4 className="text-[10px] text-[#00ffd2] uppercase font-bold tracking-widest">[ Configuration Fields ]</h4>
                
                <div className="space-y-3">
                  <div>
                    <label className="text-[9px] text-zinc-500 uppercase block mb-1">Company Legal Dec Name</label>
                    <input 
                      type="text" 
                      value={privacyBuilderForm.companyName} 
                      onChange={(e) => setPrivacyBuilderForm({ ...privacyBuilderForm, companyName: e.target.value })} 
                      className="w-full bg-black border border-zinc-800 text-white rounded px-2.5 py-1.5"
                    />
                  </div>
                  <div>
                    <label className="text-[9px] text-zinc-500 uppercase block mb-1">Company Website URL</label>
                    <input 
                      type="text" 
                      value={privacyBuilderForm.websiteUrl} 
                      onChange={(e) => setPrivacyBuilderForm({ ...privacyBuilderForm, websiteUrl: e.target.value })} 
                      className="w-full bg-black border border-zinc-800 text-white rounded px-2.5 py-1.5"
                    />
                  </div>
                  <div>
                    <label className="text-[9px] text-zinc-500 uppercase block mb-1">Public Contact Email</label>
                    <input 
                      type="text" 
                      value={privacyBuilderForm.contactEmail} 
                      onChange={(e) => setPrivacyBuilderForm({ ...privacyBuilderForm, contactEmail: e.target.value })} 
                      className="w-full bg-black border border-zinc-800 text-white rounded px-2.5 py-1.5"
                    />
                  </div>
                  <div>
                    <label className="text-[9px] text-zinc-500 uppercase block mb-1">Data Controller Details</label>
                    <input 
                      type="text" 
                      value={privacyBuilderForm.controllerDetails} 
                      onChange={(e) => setPrivacyBuilderForm({ ...privacyBuilderForm, controllerDetails: e.target.value })} 
                      className="w-full bg-black border border-zinc-800 text-white rounded px-2.5 py-1.5"
                    />
                  </div>
                  <div>
                    <label className="text-[9px] text-zinc-500 uppercase block mb-1">Data Protection Contact (DPO)</label>
                    <input 
                      type="text" 
                      value={privacyBuilderForm.dpoContact} 
                      onChange={(e) => setPrivacyBuilderForm({ ...privacyBuilderForm, dpoContact: e.target.value })} 
                      className="w-full bg-black border border-zinc-800 text-white rounded px-2.5 py-1.5"
                    />
                  </div>
                  <div>
                    <label className="text-[9px] text-zinc-500 uppercase block mb-1">Retention Period (Years)</label>
                    <input 
                      type="number" 
                      value={privacyBuilderForm.retentionYears} 
                      onChange={(e) => setPrivacyBuilderForm({ ...privacyBuilderForm, retentionYears: e.target.value })} 
                      className="w-full bg-black border border-zinc-800 text-white rounded px-2.5 py-1.5"
                    />
                  </div>
                </div>

                <div className="flex gap-2.5 pt-2">
                  <button 
                    onClick={handlePublishPrivacyPolicy}
                    className="flex-1 px-4 py-2 bg-gradient-to-r from-[#e840f3] to-[#00ffd2] text-black font-extrabold rounded-xl active:scale-95 transition hover:opacity-90 cursor-pointer text-center text-[10px] uppercase"
                  >
                    🚀 Publish Draft to Public
                  </button>
                </div>
              </div>

              {/* LIVE DOCUMENT PREVIEW */}
              <div className="space-y-4 bg-black/60 p-5 rounded-xl border border-zinc-900 overflow-y-auto max-h-[420px] text-left">
                <div className="flex justify-between items-center text-[9px] border-b border-zinc-900 pb-1.5 text-zinc-500">
                  <span>PREVIEW GENERATION</span>
                  <span>VERSION 1.2</span>
                </div>
                
                <div className="space-y-3.5 text-[10.5px] leading-relaxed text-zinc-300">
                  <h4 className="text-sm font-bold text-white uppercase">{privacyBuilderForm.companyName} Privacy Policy</h4>
                  <p className="text-[9.5px] text-zinc-500">Last updated: 2026-06-14 | Status: <strong className="text-yellow-500 uppercase">{legalReview?.privacyPolicy?.status || 'Draft'}</strong></p>

                  <p><strong>1. Company & Identity</strong><br />
                  This policy outlines how data is gathered and stored on <strong>{privacyBuilderForm.websiteUrl}</strong> by the controller, <strong>{privacyBuilderForm.controllerDetails}</strong>, contactable via <strong>{privacyBuilderForm.contactEmail}</strong> and DPO <strong>{privacyBuilderForm.dpoContact}</strong>.
                  All operations comply with lawful databases.
                  </p>

                  <p><strong>2. Core Scope & Data Gathered</strong><br />
                  We log active candidate tracks, security authorizations, search credentials, newsletter choices, and client campaign preferences. Specifically, our system elements monitor:
                  </p>
                  <ul className="list-disc list-inside space-y-1 pl-1 text-[10px] text-zinc-400">
                    <li><strong>Nexus Account Registration:</strong> Basic profiles and validation.</li>
                    <li><strong>Nexus Search usage:</strong> Tracking metering usage slots.</li>
                    <li><strong>Nexus Galaxy Lab usage:</strong> Simulated environment parameters.</li>
                    <li><strong>Nexus Chat conversations:</strong> Customer support records.</li>
                    <li><strong>Nexus Mail & Domains:</strong> Domain records.</li>
                    <li><strong>WorkSuite records:</strong> Team actions indices.</li>
                    <li><strong>Media Studio assets:</strong> Generated structures.</li>
                    <li><strong>AI Ad / UGC Content Creator:</strong> Simulated marketing logs.</li>
                    <li><strong>Social automation:</strong> Connected platform webhooks.</li>
                    <li><strong>NexoraX Billing elements:</strong> MAS disclaimers, metering logs.</li>
                  </ul>

                  <p><strong>3. Dynamic Community & Purpose Lawful Basis</strong><br />
                  Telemetry tracking maps user sessions securely. Data is retained for <strong>{privacyBuilderForm.retentionYears} years</strong> for legal compliance audits before permanent erasure. Marketing lists and outbound follow-up logs map individual explicit consents.
                  </p>

                  <div className="p-3 bg-red-950/20 border border-red-500/10 rounded text-[9.5px] text-red-400">
                    ⚠️ <strong>Warning:</strong> This document is generated for business readiness and should be reviewed by a qualified solicitor before actual legal reliance.
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* SECTION 3: COOKIE POLICY BUILDER */}
        {currentSection === 'COOKIE_BUILDER' && (
          <motion.div 
            key="cookie-builder-sec"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="bg-black/60 border border-white/10 rounded-2xl p-6 text-left space-y-5"
          >
            <div className="flex justify-between items-center border-b border-white/5 pb-3">
              <div>
                <h3 className="text-sm font-black font-sans uppercase tracking-wider text-white">🍪 Cookie Policy Builder & Categories</h3>
                <p className="text-[10px] font-mono text-zinc-400">Manage required first-party tracking cookie scopes and compliance attributes.</p>
              </div>
              <button 
                onClick={() => handleUpdateDocStatus('cookiePolicy', 'Published', true)}
                className="px-3.5 py-1.5 bg-gradient-to-r from-[#e840f3] to-[#00ffd2] text-black font-extrabold rounded-xl font-mono text-[9px] uppercase cursor-pointer"
              >
                Approve & Publish Policy
              </button>
            </div>

            {/* List of default Cookie definitions */}
            <div className="space-y-4">
              <h4 className="text-xs uppercase font-mono tracking-widest text-[#00ffd2] font-black">[ Configured Crawler Categories ]</h4>
              
              <div className="grid grid-cols-1 gap-3.5 font-mono text-[10px]">
                {scannerItems.map((c, idx) => (
                  <div key={idx} className="p-4 rounded-xl border border-white/5 bg-zinc-950/70 space-y-2.5 flex flex-col md:flex-row md:items-center justify-between gap-3">
                    <div className="space-y-1 pr-4">
                      <div className="flex items-center gap-2">
                        <strong className="text-white text-xs">{c.name}</strong>
                        <span className="px-2 py-0.5 rounded text-[8px] font-bold uppercase bg-zinc-900 text-slate-300 border border-zinc-800">
                          {c.type}
                        </span>
                      </div>
                      <p className="text-zinc-450 text-[10.5px] leading-normal">{c.purpose}</p>
                      <div className="flex flex-wrap items-center gap-3 text-[9px] text-zinc-500">
                        <span>Provider: <strong className="text-zinc-300">{c.provider}</strong></span>
                        <span>•</span>
                        <span>Category: <strong className="text-[#00ffd2]">{c.category}</strong></span>
                        <span>•</span>
                        <span>Risk level: <strong className={c.riskLevel === 'Medium' ? 'text-yellow-450' : 'text-emerald-400'}>{c.riskLevel}</strong></span>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2 mn-w-max self-start md:self-center">
                      <div className="text-right text-[9px] space-y-0.5 pr-1.5 hidden md:block">
                        <div className="text-zinc-400">Consent required: {c.consentRequired}</div>
                        <div className="text-zinc-500">Essential: {c.essential}</div>
                      </div>
                      <span className={`px-2 py-0.5 rounded font-black text-[8px] uppercase border ${c.reviewStatus === 'Approved' ? 'bg-emerald-950/20 border-emerald-500/20 text-emerald-450' : 'bg-yellow-950/20 border-yellow-500/30 text-yellow-500'}`}>
                        {c.reviewStatus}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {/* SECTION 5: COOKIE SETTINGS / PREFERENCE CENTER */}
        {currentSection === 'PREFERENCE_CENTER' && (
          <motion.div 
            key="preference-center-sec"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="bg-black/60 border border-white/10 rounded-2xl p-6 text-left space-y-5"
          >
            <div className="flex justify-between items-center border-b border-white/5 pb-3">
              <div>
                <h3 className="text-sm font-black font-sans uppercase tracking-wider text-white">🔧 Public Cookie Settings & Preference Center</h3>
                <p className="text-[10px] font-mono text-zinc-400">Configure public preference center toggles mapping opt-in consent matrices.</p>
              </div>
              <span className="text-[9px] font-mono uppercase bg-[#00ffd2]/10 border border-[#00ffd2]/25 px-2.5 py-1 rounded-xl text-[#00ffd2]">Visitor Settings</span>
            </div>

            {/* PREF TOGGLES ROW */}
            <div className="space-y-4 bg-[#02050b] p-5 rounded-2xl border border-zinc-900 font-mono text-xs text-slate-300">
              
              <div className="space-y-4 divide-y divide-zinc-900">
                
                {/* STRICTLY NECESSARY */}
                <div className="flex justify-between items-center pt-2.5">
                  <div className="space-y-1 max-w-[80%]">
                    <strong className="text-white uppercase block tracking-wider font-bold">strictly necessary storage (Default Active)</strong>
                    <p className="text-zinc-500 text-[10px] leading-normal font-mono">
                      Needed to securely manage login validations, role state checking, cookie preference registry records, and standard accounting session calculations. Cannot be disabled.
                    </p>
                  </div>
                  <span className="px-3 py-1 bg-zinc-900 border border-zinc-800 text-slate-400 rounded-lg text-[9px] font-bold uppercase">Locked</span>
                </div>

                {/* ANALYTICS OPT-IN */}
                <div className="flex justify-between items-center pt-4">
                  <div className="space-y-1 max-w-[80%]">
                    <strong className="text-white uppercase block tracking-wider font-bold">Analytics & Metrics Tracking</strong>
                    <p className="text-zinc-500 text-[10px] leading-normal font-mono">
                      Enables tracking landing page views, promo video clicks, search credit interest clicks, and general telemetry statistics. Essential to scale the platform.
                    </p>
                  </div>
                  <button 
                    onClick={() => setUserPreferences({ ...userPreferences, analytics: !userPreferences.analytics })}
                    className={`px-4 py-1.5 rounded-lg border font-bold text-[9px] uppercase cursor-pointer ${userPreferences.analytics ? 'bg-emerald-950/35 border-emerald-500 text-emerald-450' : 'bg-transparent border-zinc-800 text-zinc-550'}`}
                  >
                    {userPreferences.analytics ? 'Accepted' : 'Rejected'}
                  </button>
                </div>

                {/* MARKETING OPT-IN */}
                <div className="flex justify-between items-center pt-4">
                  <div className="space-y-1 max-w-[80%]">
                    <strong className="text-white uppercase block tracking-wider font-bold">Marketing and Campaign Analytics</strong>
                    <p className="text-zinc-500 text-[10px] leading-normal font-mono">
                      Allows us to target and analyze direct ads within the Nexus ad factory, measuring high-conversion impressions.
                    </p>
                  </div>
                  <button 
                    onClick={() => setUserPreferences({ ...userPreferences, marketing: !userPreferences.marketing })}
                    className={`px-4 py-1.5 rounded-lg border font-bold text-[9px] uppercase cursor-pointer ${userPreferences.marketing ? 'bg-emerald-950/35 border-emerald-500 text-emerald-450' : 'bg-transparent border-zinc-800 text-zinc-550'}`}
                  >
                    {userPreferences.marketing ? 'Accepted' : 'Rejected'}
                  </button>
                </div>

                {/* NEWSLETTER PREFS */}
                <div className="flex justify-between items-center pt-4">
                  <div className="space-y-1 max-w-[80%]">
                    <strong className="text-white uppercase block tracking-wider font-bold">Newsletter & Promo Bulletins Opt-In</strong>
                    <p className="text-zinc-500 text-[10px] leading-normal font-mono">
                      Unlocks email delivery for technical digests, security reports, and occasional product transformation bulletins.
                    </p>
                  </div>
                  <button 
                    onClick={() => setUserPreferences({ ...userPreferences, newsletter: !userPreferences.newsletter })}
                    className={`px-4 py-1.5 rounded-lg border font-bold text-[9px] uppercase cursor-pointer ${userPreferences.newsletter ? 'bg-emerald-950/35 border-emerald-500 text-emerald-450' : 'bg-transparent border-zinc-800 text-zinc-550'}`}
                  >
                    {userPreferences.newsletter ? 'Accepted' : 'Rejected'}
                  </button>
                </div>

                {/* CHAT CHANNELS FOLLOW-UP */}
                <div className="flex justify-between items-center pt-4">
                  <div className="space-y-1 max-w-[80%]">
                    <strong className="text-white uppercase block tracking-wider font-bold">Optional Interactive Chat Personalisation</strong>
                    <p className="text-zinc-500 text-[10px] leading-normal font-mono">
                      Records active chat responses history context to provide faster and personalized supervisor assistant responses.
                    </p>
                  </div>
                  <button 
                    onClick={() => setUserPreferences({ ...userPreferences, chatFollowUp: !userPreferences.chatFollowUp })}
                    className={`px-4 py-1.5 rounded-lg border font-bold text-[9px] uppercase cursor-pointer ${userPreferences.chatFollowUp ? 'bg-emerald-950/35 border-emerald-500 text-emerald-450' : 'bg-transparent border-zinc-800 text-zinc-550'}`}
                  >
                    {userPreferences.chatFollowUp ? 'Accepted' : 'Rejected'}
                  </button>
                </div>

              </div>

              <div className="pt-4 flex justify-end">
                <button 
                  onClick={handleSavePreferences}
                  className="px-5 py-2 hover:opacity-90 bg-gradient-to-r from-[#e840f3] to-[#00ffd2] text-black font-extrabold rounded-xl uppercase text-[10px] active:scale-95 transition cursor-pointer"
                >
                  💾 Save and Apply Preferred choices
                </button>
              </div>

            </div>
          </motion.div>
        )}
        {currentSection === 'CONSENT_REGS' && (
          <motion.div 
            key="consent-regs-sec"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="bg-black/60 border border-white/10 rounded-2xl p-6 text-left space-y-5"
          >
            <div className="flex justify-between items-center border-b border-white/5 pb-3">
              <div>
                <h3 className="text-sm font-black font-sans uppercase tracking-wider text-white">🛡️ First-Party Consent Audit Log Register (Admin Confidential)</h3>
                <p className="text-[10px] font-mono text-zinc-400">Verifiable, cryptographic database log capturing active compliance indicators.</p>
              </div>
              <span className="text-[9px] font-mono uppercase bg-red-950 border border-red-500/30 text-red-400 px-3 py-1.5 rounded-xl font-bold">Confidential Vault</span>
            </div>

            {(() => {
              const filteredLogs = consentLogs.filter(log => {
                const searchTarget = `${log.consent_id || ""} ${log.user_id || ""} ${log.anonymous_session_id || ""} ${log.ip_hash || ""} ${log.source_page || ""}`.toLowerCase();
                const matchesSearch = searchTarget.includes(consentSearchQuery.toLowerCase());
                if (consentFilterStatus === "ALL") return matchesSearch;
                return matchesSearch && log.withdrawal_status?.toUpperCase() === consentFilterStatus;
              });

              return (
                <div className="space-y-4">
                  <div className="p-3 bg-zinc-950 rounded-xl text-[10.5px] font-mono leading-relaxed text-zinc-400 border border-zinc-900">
                    🔒 <strong>Cryptographic Isolation Standard:</strong> To secure client privacy, IP addresses and browser User Agents are exclusively saved as hashed SHA-256 strings. Optional usage entries are blocked before handshake validation.
                  </div>

                  {/* SEARCH AND FILTER CONTROL PANEL */}
                  <div className="flex flex-col sm:flex-row justify-between items-center gap-3 p-3 bg-zinc-950/85 rounded-xl border border-zinc-900 font-mono text-[9px]">
                    <div className="flex items-center gap-2 w-full sm:w-auto">
                      <span className="text-zinc-500 uppercase">Search:</span>
                      <input
                        type="text"
                        value={consentSearchQuery}
                        onChange={(e) => setConsentSearchQuery(e.target.value)}
                        placeholder="Search ID, Session, URL source, IP hash..."
                        className="flex-1 bg-black border border-zinc-800 px-2.5 py-1.5 text-white placeholder-zinc-650 rounded focus:border-[#00ffd2] focus:outline-none min-w-[220px]"
                      />
                    </div>
                    <div className="flex items-center gap-2 self-start sm:self-auto">
                      <span className="text-zinc-500 uppercase">Withdraw State:</span>
                      <div className="flex gap-1">
                        {["ALL", "ACTIVE", "WITHDRAWED"].map((st) => {
                          const label = st === "WITHDRAWED" ? "Withdrawn" : st;
                          const filterMatch = st === "WITHDRAWED" ? "WITHDRAWN" : st;
                          return (
                            <button
                              key={st}
                              onClick={() => setConsentFilterStatus(filterMatch)}
                              className={`px-2.5 py-1 rounded text-[8.5px] font-bold uppercase transition ${consentFilterStatus === filterMatch ? 'bg-[#00ffd2]/10 border border-[#00ffd2]/40 text-[#00ffd2]' : 'bg-transparent border border-zinc-850 text-zinc-400 hover:text-zinc-200'}`}
                            >
                              {label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full text-left font-mono text-[9px] text-[#00ffd2]">
                      <thead>
                        <tr className="border-b border-zinc-900 text-zinc-500 uppercase">
                          <th className="py-2 pr-2">ID</th>
                          <th className="py-2 pr-2">User / Session ID</th>
                          <th className="py-2 pr-2">Accepted Categories</th>
                          <th className="py-2 pr-2">Source</th>
                          <th className="py-2 pr-2">IP Hash Match</th>
                          <th className="py-2 pr-2">State</th>
                          <th className="py-2 text-right">Instant Withdraw</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredLogs.length === 0 ? (
                          <tr>
                            <td colSpan={7} className="py-8 text-center text-zinc-500">
                              No verifiable consent log records match structural filters.
                            </td>
                          </tr>
                        ) : (
                          filteredLogs.map((log, index) => (
                            <tr key={index} className="border-b border-zinc-900/60 hover:bg-zinc-950/40">
                              <td className="py-2.5 pr-2 font-bold text-white">{log.consent_id}</td>
                              <td className="py-2.5 pr-2 text-zinc-350 max-w-[120px] truncate" title={log.user_id || log.anonymous_session_id}>
                                {log.user_id || log.anonymous_session_id}
                              </td>
                              <td className="py-2.5 pr-2 text-[8px] max-w-xs overflow-hidden flex flex-wrap gap-1">
                                {log.categories_accepted?.map((cat: string, i: number) => (
                                  <span key={i} className="px-1.5 py-0.5 rounded bg-zinc-900 border border-zinc-800 text-zinc-400 font-bold">{cat}</span>
                                ))}
                              </td>
                              <td className="py-2.5 pr-2 text-zinc-500">{log.source_page}</td>
                              <td className="py-2.5 pr-2 text-zinc-500 max-w-[100px] truncate" title={log.ip_hash}>{log.ip_hash}</td>
                              <td className="py-2.5 pr-2">
                                <span className={`px-2 py-0.5 rounded text-[8px] font-bold ${log.withdrawal_status === 'Active' ? 'bg-emerald-950/20 border border-emerald-500/25 text-emerald-400' : 'bg-red-950/20 border border-red-500/30 text-red-400'}`}>
                                  {log.withdrawal_status}
                                </span>
                              </td>
                              <td className="py-2.5 text-right font-mono">
                                {log.withdrawal_status === 'Active' ? (
                                  <button 
                                    onClick={() => handleWithdrawConsentLog(log.consent_id)}
                                    className="px-2 py-1 bg-red-950/40 text-red-450 hover:bg-red-900 border border-red-900/30 rounded text-[8px] uppercase active:scale-95 transition cursor-pointer"
                                  >
                                    Withdraw
                                  </button>
                                ) : (
                                  <span className="text-zinc-600 text-[8px]">Closed</span>
                                )}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })()}
          </motion.div>
        )}

        {/* SECTION 7: TERMS & CONDITIONS */}
        {currentSection === 'TERMS' && (
          <motion.div 
            key="terms-builder-sec"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="bg-black/60 border border-white/10 rounded-2xl p-6 text-left space-y-5"
          >
            <div className="flex justify-between items-center border-b border-white/5 pb-3">
              <div>
                <h3 className="text-sm font-black font-sans uppercase tracking-wider text-white">⚖️ Terms & Conditions Builder</h3>
                <p className="text-[10px] font-mono text-zinc-400">Manage acceptable platform usage covenants and direct corporate fee allocations.</p>
              </div>
              <span className="text-[9px] font-mono uppercase bg-yellow-950/50 border border-yellow-500/20 text-yellow-500 px-3 py-1.5 rounded-xl font-bold">Legal Review Recommended</span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 font-mono text-xs text-slate-300">
              <div className="space-y-4 bg-zinc-950/80 p-5 rounded-xl border border-zinc-900">
                <h4 className="text-[10px] text-[#00ffd2] uppercase font-bold tracking-widest">[ Specific Policy Elements ]</h4>
                
                <div className="space-y-3">
                  <div>
                    <label className="text-[9px] text-zinc-500 uppercase block mb-1">10% Maintenance & Hosting Fee Covenant</label>
                    <textarea 
                      type="text" 
                      value={termsForm.maintenanceFee} 
                      onChange={(e) => setTermsForm({ ...termsForm, maintenanceFee: e.target.value })} 
                      rows={3}
                      className="w-full bg-black border border-zinc-800 text-white rounded px-2.5 py-1.5"
                    />
                  </div>
                  <div>
                    <label className="text-[9px] text-zinc-500 uppercase block mb-1">3% Community Allocation Statement</label>
                    <input 
                      type="text" 
                      value={termsForm.communityAllocation} 
                      onChange={(e) => setTermsForm({ ...termsForm, communityAllocation: e.target.value })} 
                      className="w-full bg-black border border-zinc-800 text-white rounded px-2.5 py-1.5"
                    />
                  </div>
                  <div>
                    <label className="text-[9px] text-zinc-500 uppercase block mb-1">Search credits Session Limit</label>
                    <input 
                      type="text" 
                      value={termsForm.searchLimitPerSession} 
                      onChange={(e) => setTermsForm({ ...termsForm, searchLimitPerSession: e.target.value })} 
                      className="w-full bg-black border border-zinc-800 text-white rounded px-2.5 py-1.5"
                    />
                  </div>
                  <div>
                    <label className="text-[9px] text-zinc-500 uppercase block mb-1">Governing Jurisdiction Placeholder</label>
                    <input 
                      type="text" 
                      value={termsForm.governingLaw} 
                      onChange={(e) => setTermsForm({ ...termsForm, governingLaw: e.target.value })} 
                      className="w-full bg-black border border-zinc-800 text-white rounded px-2.5 py-1.5"
                    />
                  </div>
                </div>

                <div className="flex items-center gap-2 pt-1">
                  <input 
                    type="checkbox" 
                    id="approve_terms"
                    checked={termsForm.approvedByAdmin}
                    onChange={(e) => {
                      setTermsForm({ ...termsForm, approvedByAdmin: e.target.checked });
                      handleUpdateDocStatus('termsAndConditions', e.target.checked ? 'Approved for public preview' : 'Draft', e.target.checked);
                    }}
                    className="w-4 h-4 accent-[#00ffd2]"
                  />
                  <label htmlFor="approve_terms" className="text-[10px] text-zinc-300">Approve document status for public preview</label>
                </div>
              </div>

              {/* LIVE TERMS DEMO VIEW */}
              <div className="space-y-4 bg-black/60 p-5 rounded-xl border border-zinc-900 overflow-y-auto max-h-[420px] text-left text-zinc-400 text-[10px] leading-relaxed">
                <div className="flex justify-between items-center text-[9px] border-b border-zinc-900 pb-1.5">
                  <span className="text-white font-bold">NEXORAX TERMS OF SERVICE</span>
                  <span className="text-[#00ffd2]">PREVIEW</span>
                </div>

                <p>Welcome to NexoraX. By logging into any NexoraX dashboard terminal, you agree to these legal conditions.</p>

                <p><strong>1. Acceptable Use of Nexus AI Modules</strong><br />
                All generated components remain sandboxed. Generative agents, search inputs, and Galaxy Lab computations are bounded with strict session thresholds. Users must monitor or human-approve all outbound automation tasks before live campaign execution.
                </p>

                <p><strong>2. Search Credit Caps Map</strong><br />
                Session tracks are metered dynamically. Usage is limited to <strong>{termsForm.searchLimitPerSession}</strong> before supervisor checks.
                </p>

                <p><strong>3. Maintenance, Hosting & Community Allocation Covenants</strong><br />
                A <strong>{termsForm.maintenanceFee}</strong> applies to maintain platform integrity, cloud nodes, and active workflows. 
                Additionally, <strong>{termsForm.communityAllocation}</strong> tracks.
                </p>

                <p><strong>4. Governing Jurisdiction</strong><br />
                This contract is governed in accordance with parameters set under <strong>{termsForm.governingLaw}</strong>.
                </p>

                <div className="p-3.5 bg-yellow-950/20 border border-yellow-500/20 rounded text-[9.5px] text-yellow-400">
                  ⚠️ <strong>Disclaimer notice:</strong> Solicitor review is recommended before actual legal reliance on this terms structure template.
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* SECTION 8: REFUND & CANCELLATION */}
        {currentSection === 'REFUND_POLICY' && (
          <motion.div 
            key="refund-sec"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="bg-black/60 border border-white/10 rounded-2xl p-6 text-left space-y-5"
          >
            <div className="flex justify-between items-center border-b border-white/5 pb-3">
              <div>
                <h3 className="text-sm font-black font-sans uppercase tracking-wider text-white">💰 Refund & Cancellation Policy Builder</h3>
                <p className="text-[10px] font-mono text-zinc-400">Track cancellation stop-conditions and direct maintenance service terminations.</p>
              </div>
              <button 
                onClick={() => handleUpdateDocStatus('refundPolicy', 'Approved for public preview', true)}
                className="px-3.5 py-1.5 bg-[#00ffd2]/10 border border-[#00ffd2]/30 text-[#00ffd2] font-black rounded-xl font-mono text-[9px] uppercase cursor-pointer"
              >
                Approve Refund Draft
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 font-mono text-xs">
              <div className="space-y-4 bg-zinc-950/80 p-5 rounded-xl border border-zinc-900 space-y-4">
                <h4 className="text-[10px] text-[#00ffd2] uppercase font-bold tracking-widest">[ Parameters & Conditions ]</h4>
                
                <div className="space-y-3.5 divide-y divide-zinc-900">
                  <div className="flex justify-between items-center pt-1.5">
                    <span className="text-zinc-400">Are design / search credits refundable?</span>
                    <button 
                      onClick={() => setRefundForm({ ...refundForm, creditsRefundable: !refundForm.creditsRefundable })}
                      className={`px-3 py-1 rounded text-[10px] uppercase font-bold border ${refundForm.creditsRefundable ? 'bg-emerald-950/30 border-emerald-500/30 text-emerald-400' : 'bg-transparent border-zinc-800 text-zinc-500'}`}
                    >
                      {refundForm.creditsRefundable ? 'Yes' : 'No'}
                    </button>
                  </div>

                  <div className="flex justify-between items-center pt-3">
                    <span className="text-zinc-400">NexoraX Deployment Fee Refundable?</span>
                    <button 
                      onClick={() => setRefundForm({ ...refundForm, deploymentSetupFeeRefundable: !refundForm.deploymentSetupFeeRefundable })}
                      className={`px-3 py-1 rounded text-[10px] uppercase font-bold border ${refundForm.deploymentSetupFeeRefundable ? 'bg-emerald-950/30 border-emerald-500/30 text-emerald-400' : 'bg-transparent border-zinc-800 text-zinc-500'}`}
                    >
                      {refundForm.deploymentSetupFeeRefundable ? 'Yes' : 'No'}
                    </button>
                  </div>

                  <div className="pt-3 space-y-1">
                    <label className="text-[9px] text-zinc-500 uppercase block">Required Notice Period (Days)</label>
                    <input 
                      type="number" 
                      value={refundForm.subscriptionsNoticeDays} 
                      onChange={(e) => setRefundForm({ ...refundForm, subscriptionsNoticeDays: e.target.value })} 
                      className="w-full bg-black border border-zinc-800 text-white rounded px-2.5 py-1 text-[11px]"
                    />
                  </div>

                  <div className="pt-3 space-y-1">
                    <label className="text-[9px] text-zinc-500 uppercase block">Maintenance Stop Condition Statement</label>
                    <textarea 
                      value={refundForm.maintenanceFeeNotice} 
                      onChange={(e) => setRefundForm({ ...refundForm, maintenanceFeeNotice: e.target.value })} 
                      rows={5}
                      className="w-full bg-black border border-zinc-800 text-white rounded px-2.5 py-1 text-[11px]"
                    />
                  </div>
                </div>
              </div>

              {/* DEMO DISPLAY REFUNDS */}
              <div className="p-5 rounded-xl border border-zinc-900 bg-black/40 text-left text-[10px] leading-relaxed text-zinc-400 space-y-3 pb-4">
                <strong className="text-white uppercase text-[10.5px] block border-b border-zinc-900 pb-1 font-sans">Public Refund & Cancellation Template</strong>
                <p>We configure bespoke hosting nodes and server deployments. Due to immediate allocations, the following applies:</p>
                <ul className="list-disc list-inside space-y-1 text-zinc-500">
                  <li><strong>Setup and Custom Deployment Fees:</strong> {refundForm.deploymentSetupFeeRefundable ? 'Refundable within 14 days.' : 'Non-refundable once active container is initialized.'}</li>
                  <li><strong>Search & Processing Credits:</strong> {refundForm.creditsRefundable ? 'Unused units fully refundable.' : 'Permanently non-refundable after purchase.'}</li>
                  <li><strong>Notice period:</strong> Cancel active subscriptions giving {refundForm.subscriptionsNoticeDays} days advanced notice.</li>
                </ul>

                <p><strong>Maintenance & Hosting Fee stop condition:</strong><br />
                {refundForm.maintenanceFeeNotice}
                </p>
              </div>
            </div>
          </motion.div>
        )}

        {/* SECTION 9: ACCESSIBILITY */}
        {currentSection === 'ACCESSIBILITY' && (
          <motion.div 
            key="acc-sec"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="bg-black/60 border border-white/10 rounded-2xl p-6 text-left space-y-5"
          >
            <div className="flex justify-between items-center border-b border-white/5 pb-3">
              <div>
                <h3 className="text-sm font-black font-sans uppercase tracking-wider text-white">♿ Accessibility Statement Manager</h3>
                <p className="text-[10px] font-mono text-zinc-400">Covenant tracking for high-contrast mobile readability and screen readers access.</p>
              </div>
              <button 
                onClick={() => handleUpdateDocStatus('accessibilityStatement', 'Approved for public preview', true)}
                className="px-3.5 py-1.5 bg-[#e840f3]/10 border border-[#e840f3]/30 text-[#e840f3] font-black rounded-xl font-mono text-[9px] uppercase cursor-pointer"
              >
                Approve Statement
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 font-mono text-xs">
              <div className="space-y-4 bg-zinc-950/80 p-5 rounded-xl border border-zinc-900 space-y-3.5">
                <h4 className="text-[10px] text-[#00ffd2] uppercase font-bold tracking-widest">[ Operational Covenants & Statuses ]</h4>
                
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Keyboard Navigation state:</span>
                    <span className="text-emerald-400 font-bold">{accessibilityForm.keyboardNavStatus}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Color Contrast:</span>
                    <span className="text-yellow-400 font-bold">{accessibilityForm.contrastStatus}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Form Labels & Alt Tags:</span>
                    <span className="text-yellow-400 font-bold">{accessibilityForm.altTextStatus}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Mobile readability bounds:</span>
                    <span className="text-emerald-400 font-bold">{accessibilityForm.mobileReadability}</span>
                  </div>
                </div>

                <div className="border-t border-zinc-900 pt-3.5 space-y-3">
                  <strong className="text-white text-[10px] uppercase block tracking-wider font-bold">Planned accessibility Improvement tasks:</strong>
                  <div className="grid grid-cols-2 gap-2 text-[9px]">
                    <span className="p-2 rounded bg-zinc-900 border border-zinc-805 text-zinc-300">✓ Alt visual tag verification</span>
                    <span className="p-2 rounded bg-zinc-900 border border-zinc-805 text-zinc-300">✓ Contrast ratio testing</span>
                    <span className="p-2 rounded bg-zinc-900 border border-zinc-850 text-amber-500">⚙ Keyboard focused items</span>
                    <span className="p-2 rounded bg-zinc-900 border border-zinc-850 text-amber-500">⚙ Video captions transcription</span>
                  </div>
                </div>
              </div>

              {/* OUTWARD ACCESSIBILITY STATEMENT PREVIEW */}
              <div className="p-5 rounded-xl border border-zinc-900 bg-black/40 text-[10px] leading-relaxed text-zinc-400 space-y-3 text-left">
                <strong className="text-white uppercase block border-b border-zinc-900 pb-1.5 font-bold">Public Accessibility Statement Preview</strong>
                <p><strong>Covenant Statement:</strong><br />{accessibilityForm.commitment}</p>
                <p><strong>Known Limitations:</strong><br />{accessibilityForm.limitations}</p>
                <p><strong>Reporting contact:</strong><br />In case you face any keyboard validation issues, contact our terminal support at <strong>{accessibilityForm.contact}</strong>.</p>
              </div>
            </div>
          </motion.div>
        )}

        {/* SECTION 10: DATA RIGHTS REQUEST PORTAL */}
        {currentSection === 'DATA_RIGHTS' && (
          <motion.div 
            key="rights-sec"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="bg-black/60 border border-white/10 rounded-2xl p-6 text-left space-y-5"
          >
            <div className="flex justify-between items-center border-b border-white/5 pb-3">
              <div>
                <h3 className="text-sm font-black font-sans uppercase tracking-wider text-white">📩 Public Data Rights Request Portal</h3>
                <p className="text-[10px] font-mono text-zinc-400">Secure entry point for users to request data export, deletion, or consent withdrawal.</p>
              </div>
              <span className="text-[9px] font-mono uppercase bg-blue-950 border border-blue-500/25 text-blue-400 px-3 py-1 rounded-xl">Public Portal</span>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-5 font-mono text-xs">
              
              {/* SUBMISSION FORM */}
              <form onSubmit={handleSubmitDataRequest} className="space-y-3.5 bg-zinc-950/80 p-5 rounded-xl border border-zinc-905">
                <h4 className="text-[10px] text-[#00ffd2] uppercase font-bold tracking-widest">[ Submit Privacy Request ]</h4>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-[8.5px] text-zinc-500 uppercase block mb-1">Full Name</label>
                    <input 
                      type="text" required
                      value={dataRequestForm.name} 
                      onChange={(e) => setDataRequestForm({ ...dataRequestForm, name: e.target.value })} 
                      className="w-full bg-black border border-zinc-800 text-white rounded px-2.5 py-1.5 text-[11px]"
                    />
                  </div>
                  <div>
                    <label className="text-[8.5px] text-zinc-500 uppercase block mb-1">Primary Email Address</label>
                    <input 
                      type="email" required
                      value={dataRequestForm.email} 
                      onChange={(e) => setDataRequestForm({ ...dataRequestForm, email: e.target.value })} 
                      className="w-full bg-black border border-zinc-800 text-white rounded px-2.5 py-1.5 text-[11px]"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-[8.5px] text-zinc-500 uppercase block mb-1">Request Type Selection</label>
                    <select 
                      value={dataRequestForm.requestType} 
                      onChange={(e) => setDataRequestForm({ ...dataRequestForm, requestType: e.target.value })} 
                      className="w-full bg-black border border-zinc-800 text-[#00ffd2] rounded px-2 py-1.5 text-[11px] font-bold"
                    >
                      <option value="Access my data">Access my data</option>
                      <option value="Correct my data">Correct my data</option>
                      <option value="Delete my data">Delete my data</option>
                      <option value="Export my data">Export my data</option>
                      <option value="Withdraw consent">Withdraw consent</option>
                      <option value="Unsubscribe from marketing">Unsubscribe from marketing</option>
                      <option value="Restrict processing">Restrict processing</option>
                      <option value="Object to processing">Object to processing</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[8.5px] text-zinc-500 uppercase block mb-1">Alternative Account Email (Optional)</label>
                    <input 
                      type="email" 
                      value={dataRequestForm.altEmail} 
                      onChange={(e) => setDataRequestForm({ ...dataRequestForm, altEmail: e.target.value })} 
                      className="w-full bg-black border border-zinc-800 text-white rounded px-2.5 py-1.5 text-[11px]"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-[8.5px] text-zinc-500 uppercase block mb-1">Detail / Message</label>
                  <textarea 
                    value={dataRequestForm.message} required
                    onChange={(e) => setDataRequestForm({ ...dataRequestForm, message: e.target.value })} 
                    rows={3} placeholder="Describe details to match your identity records..."
                    className="w-full bg-black border border-zinc-800 text-white rounded px-2.5 py-1.5 text-[11px]"
                  />
                </div>

                <div className="flex items-center gap-2">
                  <input 
                    type="checkbox" required
                    id="consent_check"
                    checked={dataRequestForm.consentChecked}
                    onChange={(e) => setDataRequestForm({ ...dataRequestForm, consentChecked: e.target.checked })}
                    className="w-4 h-4 accent-[#00ffd2]"
                  />
                  <label htmlFor="consent_check" className="text-[8.5px] text-zinc-400">
                    I authorize the processing of my email coordinates exclusively to evaluate identity verification parameters.
                  </label>
                </div>

                <button 
                  type="submit"
                  className="w-full py-2 bg-gradient-to-r from-blue-600 to-cyan-500 hover:opacity-90 font-extrabold rounded-xl uppercase text-[10px] text-black cursor-pointer"
                >
                  ✉️ Submit Data rights request
                </button>
              </form>

              {/* ADMIN QUEUE LOGS */}
              <div className="space-y-4">
                <h4 className="text-[10px] text-yellow-500 uppercase font-bold tracking-widest flex items-center gap-1.5">[ Admin Controller Ticket Queue ]</h4>
                
                {requestsList.length === 0 ? (
                  <div className="p-10 border border-zinc-900 rounded-xl bg-black/40 text-center text-zinc-500 text-[11px]">
                    No active privacy data rights tickets found in server directory.
                  </div>
                ) : (
                  <div className="space-y-3 overflow-y-auto max-h-[380px] pr-2">
                    {requestsList.map((ticket, index) => (
                      <div key={index} className="p-4 rounded-xl bg-zinc-950 border border-zinc-900 space-y-3.5 text-left text-[10px] leading-relaxed">
                        <div className="flex justify-between items-center border-b border-zinc-900 pb-1.5">
                          <strong className="text-white text-[11px] font-sans font-black uppercase flex items-center gap-1">
                            <Sliders size={12} className="text-[#00ffd2]" />
                            {ticket.requestType}
                          </strong>
                          <span className={`px-2 py-0.5 rounded text-[8px] font-bold ${ticket.status === 'Completed' ? 'bg-emerald-950/20 border border-emerald-500/20 text-emerald-400' : 'bg-amber-950/20 border border-amber-500/25 text-amber-500'}`}>
                            {ticket.status}
                          </span>
                        </div>

                        <div className="space-y-1 text-zinc-400">
                          <div>Requestor: <strong className="text-zinc-300">{ticket.name}</strong> ({ticket.email})</div>
                          <p className="text-zinc-500 text-[9.5px]">"{ticket.message}"</p>
                          <div className="text-[8px] text-zinc-650">Timestamp: {ticket.timestamp}</div>
                        </div>

                        {(activeRole === 'SUPER_ADMIN' || activeRole === 'ADMIN') && (
                          <div className="pt-2 flex flex-wrap items-center justify-between gap-1 border-t border-zinc-900">
                            <span className="text-[8.5px] text-zinc-500">Identity check: <strong className="text-white">{ticket.verificationStatus}</strong></span>
                            <div className="flex gap-1.5">
                              <button 
                                onClick={() => handleUpdateRequestStatus(ticket.id, undefined, 'Verified')}
                                className="px-2 py-0.5 bg-zinc-900 text-slate-300 rounded border border-zinc-800 text-[8px] uppercase hover:bg-zinc-800 active:scale-95 transition"
                              >
                                Mark Verified
                              </button>
                              <button 
                                onClick={() => handleUpdateRequestStatus(ticket.id, 'Completed', undefined)}
                                className="px-2 py-0.5 bg-emerald-950/30 text-emerald-450 rounded border border-emerald-500/20 text-[8px] uppercase hover:bg-emerald-900 active:scale-95 transition"
                              >
                                Complete
                              </button>
                              <button 
                                onClick={() => handleUpdateRequestStatus(ticket.id, 'Rejected with reason', undefined)}
                                className="px-2 py-0.5 bg-red-950/30 text-red-450 rounded border border-red-500/20 text-[8px] uppercase hover:bg-red-900 active:scale-95 transition"
                              >
                                Reject
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

              </div>

            </div>
          </motion.div>
        )}

        {/* SECTION 11: CHANNELS CONSENT OPT-INS */}
        {currentSection === 'MARKETING_CHANNELS' && (
          <motion.div 
            key="marketing-channels-sec"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="bg-black/60 border border-white/10 rounded-2xl p-6 text-left space-y-5"
          >
            <div className="flex justify-between items-center border-b border-white/5 pb-3">
              <div>
                <h3 className="text-sm font-black font-sans uppercase tracking-wider text-white">💌 Marketing, Newsletter & Chat Lead Consent</h3>
                <p className="text-[10px] font-mono text-zinc-400">Strict segregation of inbound enquiry consent, marketing promos opt-in, and interactive chat follow-up permissions.</p>
              </div>
              <span className="text-[9px] font-mono uppercase bg-[#00ffd2]/12 border border-[#00ffd2]/30 px-3 py-1.5 rounded-xl font-bold">Unbundled Opt-In</span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 font-mono text-xs">
              
              {/* SUBMISSION LEAD FORM */}
              <form onSubmit={handleMarketingCaptureSubmit} className="space-y-3.5 bg-zinc-950/80 p-5 rounded-xl border border-zinc-900">
                <h4 className="text-[10px] text-[#e840f3] uppercase font-bold tracking-widest">[ Unbundled Inbound Lead Capture Simulator ]</h4>
                
                <div className="space-y-3 text-[10.5px]">
                  <div>
                    <label className="text-[9px] text-zinc-500 uppercase block mb-1">Company / Team Name</label>
                    <input 
                      type="text" required
                      value={leadCaptureForm.company} 
                      onChange={(e) => setLeadCaptureForm({ ...leadCaptureForm, company: e.target.value })} 
                      className="w-full bg-black border border-zinc-800 text-white rounded px-2.5 py-1 text-[11px]"
                    />
                  </div>
                  <div>
                    <label className="text-[9px] text-zinc-500 uppercase block mb-1">Business Contact Email</label>
                    <input 
                      type="email" required
                      value={leadCaptureForm.email} 
                      onChange={(e) => setLeadCaptureForm({ ...leadCaptureForm, email: e.target.value })} 
                      className="w-full bg-black border border-zinc-800 text-white rounded px-2.5 py-1 text-[11px]"
                    />
                  </div>
                  
                  {/* UNBUNDLED CHECKBOXES */}
                  <div className="p-3 bg-black border border-zinc-900 rounded-xl space-y-3">
                    <strong className="text-white text-[9.5px] uppercase block tracking-wider font-bold">Explicit Compliance Opt-In Framework:</strong>
                    
                    {/* CHECKBOX 1 */}
                    <div className="flex items-start gap-2.5 pt-1">
                      <input 
                        type="checkbox" required
                        id="enquiry_consent"
                        checked={leadCaptureForm.enquiryConsent}
                        onChange={(e) => setLeadCaptureForm({ ...leadCaptureForm, enquiryConsent: e.target.checked })}
                        className="w-4 h-4 accent-[#00ffd2] mt-0.5"
                      />
                      <label htmlFor="enquiry_consent" className="text-[9px] text-zinc-300 leading-snug">
                        <strong>Required enquiry Consent:</strong> I authorize NexoraX team support staff to email me explicitly regarding my inbound business enquiry.
                      </label>
                    </div>

                    {/* CHECKBOX 2 */}
                    <div className="flex items-start gap-2.5 pt-1.5 border-t border-zinc-905">
                      <input 
                        type="checkbox"
                        id="marketing_consent"
                        checked={leadCaptureForm.marketingConsent}
                        onChange={(e) => setLeadCaptureForm({ ...leadCaptureForm, marketingConsent: e.target.checked })}
                        className="w-4 h-4 accent-[#00ffd2] mt-0.5"
                      />
                      <label htmlFor="marketing_consent" className="text-[9px] text-zinc-400 leading-snug">
                        <strong>Optional Marketing Consent (Not Pre-ticked):</strong> I wish to receive platform transformations, promotional product bulletins, and AI ad factory updates.
                      </label>
                    </div>
                  </div>

                </div>

                <button 
                  type="submit"
                  className="w-full py-2 bg-gradient-to-r from-[#e840f3] to-[#00ffd2] hover:opacity-90 text-black font-extrabold rounded-xl uppercase text-[10px] cursor-pointer"
                >
                  📩 Submit Inbound Lead
                </button>
              </form>

              {/* COMPLIANCE GUIDELINES SCREEN */}
              <div className="p-5 rounded-xl border border-zinc-900 bg-black/40 text-[10.5px] leading-relaxed text-zinc-400 space-y-3.5 text-left">
                <strong className="text-white uppercase text-xs block border-b border-zinc-900 pb-1.5 font-bold">[ Unbundled Marketing Consent Protocols ]</strong>
                <p>NexoraX operates on high-integrity telemetry standards under Lead Director. Our marketing consent managers strictly enforce these rules:</p>
                <ul className="list-disc list-inside space-y-1.5 text-[10px] text-zinc-500">
                  <li><strong>No Pre-ticked boxes:</strong> Direct marketing checkbox must remain unselected by default.</li>
                  <li><strong>Unsubscribe Transparency:</strong> Every outbound email includes a one-click unsubscribe token.</li>
                  <li><strong>Human approval step:</strong> Outbound campaigns require active authorization inside the Secretary Node before server dispatch.</li>
                  <li><strong>Instant Suppression:</strong> If withdrawn, marketing permissions turn off instantly on active contact directories.</li>
                </ul>
              </div>

            </div>
          </motion.div>
        )}

        {/* SECTION 12: FIRST-PARTY ANALYTICS CONSENT */}
        {currentSection === 'ANALYTICS_CONSENT' && (
          <motion.div 
            key="analytics-cons-sec"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="bg-black/60 border border-white/10 rounded-2xl p-6 text-left space-y-5"
          >
            <div className="flex justify-between items-center border-b border-white/5 pb-3">
              <div>
                <h3 className="text-sm font-black font-sans uppercase tracking-wider text-white">📊 First-Party Analytics Consent Opt-In Manager</h3>
                <p className="text-[10px] font-mono text-zinc-400">Strictly block optional preference cookies and promotional clicks before active explicit consent handshake.</p>
              </div>
              <span className="text-[9px] font-mono uppercase bg-emerald-950 border border-emerald-500/25 px-2.5 py-1 rounded-xl text-emerald-400 font-bold">Active Shield</span>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-5 font-mono text-xs">
              
              {/* CURRENT SIMULATED EVENTS LIST */}
              <div className="space-y-4">
                <h4 className="text-[10px] text-[#00ffd2] uppercase font-bold tracking-widest">[ Telemetry Event Dispatch Log ]</h4>
                
                <div className="space-y-2.5">
                  {analyticsEvents.map((e, index) => (
                    <div key={e.id} className="p-3 bg-zinc-950 border border-zinc-900 rounded-xl space-y-1.5 text-left text-[10px]">
                      <div className="flex justify-between items-center">
                        <strong className="text-white font-bold">{e.event}</strong>
                        <span className={`px-2 py-0.5 rounded text-[8px] font-bold ${e.type === 'ESSENTIAL' ? 'bg-cyan-950 text-cyan-400 border border-cyan-500/20' : 'bg-purple-950 text-purple-400 border border-purple-500/20'}`}>
                          {e.type}
                        </span>
                      </div>
                      <div className="flex justify-between items-center text-[9px] text-zinc-500">
                        <span>{e.timestamp}</span>
                        <span className={`font-bold ${e.status === 'TRACKED' ? 'text-emerald-400' : 'text-rose-450 underline animate-pulse'}`}>
                          {e.status === 'TRACKED' ? '✓ Registered' : '🚫 Gated before Consent'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* OPT-IN CONTROLLER PREFERENCES */}
              <div className="space-y-4 bg-zinc-950/80 p-5 rounded-xl border border-zinc-900 space-y-3 text-left">
                <h4 className="text-[10px] text-yellow-500 uppercase font-bold tracking-widest">[ Privacy Sandbox Covenants ]</h4>
                
                <div className="p-3.5 bg-black/50 rounded-xl border border-zinc-800 space-y-2">
                  <div className="flex justify-between items-center text-[10px]">
                    <span className="text-zinc-300">Disable all non-essential analytics?</span>
                    <button 
                      onClick={() => {
                        const nextState = !userPreferences.analytics;
                        setUserPreferences({ ...userPreferences, analytics: nextState });
                        setAnalyticsEvents(prev => prev.map(e => e.type === 'OPTIONAL' ? { ...e, status: nextState ? 'TRACKED' : 'BLOCKED_BEFORE_CONSENT' } : e));
                      }}
                      className={`px-3 py-1 rounded text-[9px] font-black uppercase border ${userPreferences.analytics ? 'bg-rose-950/30 border-rose-500/30 text-rose-400' : 'bg-transparent border-zinc-800 text-zinc-550'}`}
                    >
                      {userPreferences.analytics ? 'Active (Block)' : 'Blocked'}
                    </button>
                  </div>
                </div>

                <div className="text-[10px] text-zinc-400 leading-relaxed space-y-2">
                  <p><strong>🔒 Essential Only Policy:</strong> Prior to consent validation, our system strictly allows only:</p>
                  <ul className="list-disc list-inside space-y-1 text-zinc-500 font-mono">
                    <li>Active supervisor login or routing session validations.</li>
                    <li>Security block checks.</li>
                    <li>Checkout billing initialization.</li>
                    <li>Consent preferences saving.</li>
                    <li>System compile errors reports.</li>
                  </ul>
                  <p className="pt-2"><strong>📈 Consent-Unlocked features:</strong> Once accepted, our system unlocks landing views, video clicks, Search upgrade bookings trackers, and lead campaigns interest metrics.</p>
                </div>
              </div>

            </div>
          </motion.div>
        )}

        {/* SECTION 13: COOKIE & STORAGE SCANNER */}
        {currentSection === 'SCANNER' && (
          <motion.div 
            key="scanner-sec"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="bg-black/60 border border-white/10 rounded-2xl p-6 text-left space-y-5"
          >
            <div className="flex justify-between items-center border-b border-white/5 pb-3">
              <div>
                <h3 className="text-sm font-black font-sans uppercase tracking-wider text-white">🔍 Cookie & Client Storage Scanner</h3>
                <p className="text-[10px] font-mono text-zinc-400">Automated crawlers scanning indexed cookies, localStorage keys, and embedded analytical scripts.</p>
              </div>
              <button 
                onClick={handleTriggerScanner} disabled={isScanning}
                className="px-4 py-2 hover:opacity-90 bg-gradient-to-r from-[#00ffd2] to-cyan-500 text-black font-black rounded-xl font-mono text-[9px] uppercase cursor-pointer"
              >
                {isScanning ? 'Scanning Storage...' : 'Launch Live Crawler Scanner'}
              </button>
            </div>

            <div className="space-y-4">
              <h4 className="text-xs uppercase font-mono tracking-widest text-[#00ffd2] font-black">[ Identified Storage Properties ]</h4>
              
              <div className="overflow-x-auto font-mono text-[9.5px]">
                <table className="w-full text-left text-zinc-300">
                  <thead>
                    <tr className="border-b border-zinc-900 text-zinc-500 uppercase text-[9px]">
                      <th className="py-2">Identifier</th>
                      <th className="py-2">Type</th>
                      <th className="py-2">Category</th>
                      <th className="py-2">Provider</th>
                      <th className="py-2">Essential</th>
                      <th className="py-2">Risk</th>
                      <th className="py-2 text-right">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...scannerItems, ...scannedItems].map((item, idx) => (
                      <tr key={idx} className="border-b border-zinc-900 hover:bg-zinc-950/40">
                        <td className="py-3 font-bold text-white max-w-[200px] truncate">{item.name}</td>
                        <td className="py-3 text-[#e840f3]">{item.type}</td>
                        <td className="py-3 text-[#00ffd2]">{item.category}</td>
                        <td className="py-3 text-zinc-400">{item.provider}</td>
                        <td className="py-3">{item.essential}</td>
                        <td className="py-3">
                          <span className={`px-2 py-0.5 rounded text-[8.5px] font-bold ${item.riskLevel === 'None' ? 'text-zinc-500 bg-zinc-900' : item.riskLevel === 'Low' ? 'text-cyan-400 bg-cyan-950/20' : 'text-yellow-500 bg-yellow-950/20'}`}>
                            {item.riskLevel}
                          </span>
                        </td>
                        <td className="py-3 text-right">
                          <span className="text-[#00ffd2] font-bold">{item.reviewStatus}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </motion.div>
        )}

        {/* SECTION 15: LEGAL REVIEW DASHBOARD */}
        {currentSection === 'LEGAL_REVIEW' && (
          <motion.div 
            key="legal-review-sec"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="bg-black/60 border border-white/10 rounded-2xl p-6 text-left space-y-5"
          >
            <div className="flex justify-between items-center border-b border-white/5 pb-3">
              <div>
                <h3 className="text-sm font-black font-sans uppercase tracking-wider text-white">📜 Legal Review and Solicitor Alignment Matrix</h3>
                <p className="text-[10px] font-mono text-zinc-400">Verifies system statuses of public disclosures and required administrative reviews.</p>
              </div>
              <span className="text-[9px] font-mono bg-purple-950 text-purple-400 border border-purple-500/20 px-3 py-1 rounded-xl">Review Dashboard</span>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 font-mono text-[10px]">
                
                {Object.keys(legalReview).map((key) => {
                  const doc = legalReview[key];
                  const labelString = key.replace(/([A-Z])/g, ' $1');
                  return (
                    <div key={key} className="p-4 rounded-xl bg-zinc-950/90 border border-zinc-900 space-y-3">
                      <div className="flex justify-between items-start border-b border-zinc-900 pb-2">
                        <strong className="text-white uppercase tracking-wider text-[10.5px] font-bold block">{labelString}</strong>
                        <span className={`px-2 py-0.5 rounded text-[8px] font-bold uppercase ${doc.status === 'Draft' ? 'bg-zinc-900 text-zinc-500' : 'bg-purple-950/35 border border-purple-500/25 text-purple-400'}`}>
                          {doc.status}
                        </span>
                      </div>
                      <div className="space-y-1 text-zinc-500 text-[9px]">
                        <div>Last updated: <strong>{doc.lastUpdated}</strong></div>
                        <div>Solicitor/Auditor Approved: <strong className={doc.auditorApproved ? 'text-emerald-400' : 'text-yellow-500'}>{doc.auditorApproved ? 'Yes' : 'Review recommended'}</strong></div>
                      </div>
                      
                      {(activeRole === 'SUPER_ADMIN' || activeRole === 'ADMIN') && (
                        <div className="pt-2 max-w-full flex justify-between gap-1 border-t border-zinc-905">
                          <button 
                            onClick={() => handleUpdateDocStatus(key, 'Draft', false)}
                            className="px-2 py-1 bg-zinc-900 text-slate-400 rounded hover:bg-zinc-800 text-[8.5px] uppercase"
                          >
                            Draft
                          </button>
                          <button 
                            onClick={() => handleUpdateDocStatus(key, 'Approved for public preview', true)}
                            className="px-2.5 py-1 bg-[#00ffd2]/10 border border-[#00ffd2]/25 text-[#00ffd2] rounded hover:bg-[#00ffd2]/20 text-[8.5px] uppercase"
                          >
                            Approve
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}

              </div>

              {/* AI NEXORAX COMPLIANCE AND LEGISLATION INTEGRATOR */}
              <div className="pt-6 border-t border-white/5 space-y-4">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3">
                  <div>
                    <h4 className="text-xs font-black font-sans uppercase tracking-wider text-white">⚡ AI-Powered NexoraX Regulatory Audit</h4>
                    <p className="text-[10px] text-zinc-400 font-mono mt-0.5">Launches full verification checks on drafted policy elements using deep compliance models.</p>
                  </div>
                  <div className="flex items-center gap-2 self-start md:self-auto">
                    <select
                      value={selectedReviewKey}
                      onChange={(e) => {
                        setSelectedReviewKey(e.target.value);
                        setAiReviewResult(null);
                        setAiReviewStatus("idle");
                      }}
                      className="bg-black/80 border border-zinc-800 text-slate-350 text-[10px] rounded px-2.5 py-1.5 focus:outline-none"
                    >
                      <option value="privacyPolicy">Privacy Policy Draft</option>
                      <option value="cookiePolicy">Cookie Policy Draft</option>
                      <option value="termsAndConditions">Terms & Conditions Draft</option>
                      <option value="refundPolicy">Cancellation & Refunds Draft</option>
                      <option value="accessibilityStatement">Accessibility Statement Draft</option>
                    </select>
                    <button
                      onClick={handleTriggerAIReview}
                      disabled={aiReviewStatus === "analyzing"}
                      className="px-3 py-1.5 hover:opacity-90 bg-gradient-to-r from-[#00ffd2] to-[#e840f3] text-black font-black font-mono rounded text-[9px] uppercase cursor-pointer"
                    >
                      {aiReviewStatus === "analyzing" ? "Analyzing draft..." : "Conduct AI Legal Audit"}
                    </button>
                  </div>
                </div>

                {/* AI AUDIT RESULT */}
                {aiReviewStatus === "analyzing" && (
                  <div className="p-8 rounded-xl bg-[#00ffd2]/5 border border-[#00ffd2]/20 flex flex-col items-center justify-center space-y-4">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-[#00ffd2]"></div>
                    <p className="text-[10.5px] text-zinc-400 font-mono text-center">NexoraX Compliance engine retrieving draft states and running Scotland/UK GDPR audit checklists...</p>
                  </div>
                )}

                {aiReviewStatus === "success" && aiReviewResult && (
                  <div className="p-5 rounded-xl bg-purple-950/15 border border-[#e840f3]/30 space-y-4 text-xs font-mono">
                    <div className="flex flex-wrap justify-between items-center gap-3 border-b border-zinc-900 pb-2">
                      <div className="flex items-center gap-2 text-white font-bold">
                        <span>🛡️ COMPLIANCE RISK AUDIT STATEMENT</span>
                      </div>
                      <div className="flex items-center gap-3 text-[10px]">
                        <span>Risk level: <strong className={`uppercase ${aiReviewResult.riskLevel === 'High' ? 'text-rose-450' : aiReviewResult.riskLevel === 'Medium' ? 'text-yellow-500' : 'text-emerald-400'}`}>{aiReviewResult.riskLevel}</strong></span>
                        <span>•</span>
                        <span>System Score: <strong className="text-[#00ffd2]">{aiReviewResult.score || 100}/100</strong></span>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Missing components */}
                      <div className="p-3 rounded-lg bg-black/40 border border-zinc-900 space-y-2">
                        <strong className="text-yellow-500 text-[10px] uppercase block">⚠️ Crucial Missing Components:</strong>
                        {aiReviewResult.missingClauses && aiReviewResult.missingClauses.length > 0 ? (
                          <ul className="list-disc list-inside space-y-1 text-[9.5px] text-zinc-450">
                            {aiReviewResult.missingClauses.map((clause: string, i: number) => (
                              <li key={i}>{clause}</li>
                            ))}
                          </ul>
                        ) : (
                          <span className="text-[9.5px] text-emerald-400">No major missing regulatory clauses identified.</span>
                        )}
                      </div>

                      {/* Recommendations */}
                      <div className="p-3 rounded-lg bg-black/40 border border-zinc-900 space-y-2">
                        <strong className="text-[#00ffd2] text-[10px] uppercase block">📝 Watertight Wording Actions:</strong>
                        {aiReviewResult.recommendations && aiReviewResult.recommendations.length > 0 ? (
                          <ul className="list-disc list-inside space-y-1 text-[9.5px] text-zinc-450">
                            {aiReviewResult.recommendations.map((rec: string, i: number) => (
                              <li key={i}>{rec}</li>
                            ))}
                          </ul>
                        ) : (
                          <span className="text-[9.5px] text-zinc-500">Perfect alignment. No changes requested.</span>
                        )}
                      </div>
                    </div>

                    {/* Detailed evaluation */}
                    <div className="p-3 bg-black/60 rounded-lg border border-white/5 space-y-1">
                      <strong className="text-zinc-300 text-[10px] uppercase block">📊 Comprehensive AI Audit Briefing:</strong>
                      <div className="text-[10px] text-zinc-400 leading-relaxed whitespace-pre-wrap">
                        {aiReviewResult.detailedReport}
                      </div>
                    </div>

                    <div className="flex justify-end gap-2 pt-1 border-t border-zinc-900">
                      <button
                        onClick={() => {
                          handleUpdateDocStatus(selectedReviewKey, 'Approved for public preview', true);
                          setAiReviewStatus("idle");
                        }}
                        className="px-3.5 py-1 bg-emerald-950/40 border border-emerald-500/30 text-emerald-450 hover:bg-emerald-950/60 rounded text-[9px] uppercase cursor-pointer"
                      >
                        ✅ Acknowledge and Approve Document Status
                      </button>
                    </div>
                  </div>
                )}
              </div>

            </div>
          </motion.div>
        )}

        {/* SECTION 18: PUBLIC PREVIEW READINESS GATE */}
        {currentSection === 'READINESS_GATE' && (
          <motion.div 
            key="readiness-gate-sec"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="bg-black/60 border border-white/10 rounded-2xl p-6 text-left space-y-5"
          >
            <div className="flex justify-between items-center border-b border-white/5 pb-3">
              <div>
                <h3 className="text-sm font-black font-sans uppercase tracking-wider text-white">🚦 Final Public Preview Readiness Gate</h3>
                <p className="text-[10px] font-mono text-zinc-400">Strict regulatory inspection checking privacy policies, cookies consent state and confidential variable separation.</p>
              </div>
              <span className={`px-3 py-1 rounded-xl font-mono text-[9px] uppercase font-bold border ${gateResult.allPassed ? 'bg-emerald-950 border-emerald-500 text-emerald-400' : 'bg-rose-950 border-rose-500 text-rose-450'}`}>
                {gateResult.allPassed ? 'Gate: Passed' : 'Gate: Blockers Found'}
              </span>
            </div>

            {/* STATUS DISPLAY */}
            {gateResult.allPassed ? (
              <div className="p-5 rounded-xl border border-emerald-500/20 bg-emerald-950/20 text-xs font-mono text-emerald-400 leading-relaxed text-left space-y-2">
                <div className="flex items-center gap-2 font-black font-sans text-sm text-white uppercase">
                  <CheckCircle className="text-emerald-400" />
                  <span>NexoraX Public Preview Ready — legal footer, privacy wording, consent controls, and public-safe terminology verified.</span>
                </div>
                <p>
                  Every required first-party policy (Privacy, Cookies, Terms, Cancellation and Accessibility) is active and published. 
                  Durable server-side consent logging registries are synchronized with client preference settings toggles. 
                  Notice of recommendation for professional legal review remains recommended before final legal reliance.
                </p>
              </div>
            ) : (
              <div className="p-5 rounded-xl border border-rose-500/25 bg-rose-950/15 text-xs font-mono text-rose-350 leading-relaxed text-left space-y-3">
                <div className="flex items-center gap-2 font-black font-sans text-sm text-white uppercase">
                  <AlertCircle className="text-rose-400" />
                  <span>Inspection Blockers Identified</span>
                </div>
                <p>Public launch is restricted. Please publish draft policies and complete required checkbox validations to clear active blockers:</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[10px] font-bold">
                  {gateResult.failures.map((item, index) => (
                    <span key={index} className="p-2 rounded bg-zinc-950 border border-red-500/20 text-red-400">
                      ✗ {item.name}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* DETAILS CHECKLIST GRID */}
            <div className="space-y-4">
              <h4 className="text-xs uppercase font-mono tracking-widest text-[#00ffd2] font-black">[ Inspected Readiness Criteria Matrix ]</h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 font-mono text-[10px]">
                {gateResult.checkList.map((item, idx) => (
                  <div key={idx} className="p-3.5 rounded-xl border border-white/5 bg-zinc-950/70 flex justify-between items-center gap-3">
                    <span className="text-zinc-350">{item.name}</span>
                    <span className={`px-2 py-0.5 rounded text-[8.5px] font-bold ${item.pass ? 'bg-emerald-950 text-emerald-400 border border-emerald-500/20' : 'bg-red-950 text-red-450 border border-red-500/20'}`}>
                      {item.pass ? 'Passed' : 'Blocked'}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* LINK LEGAL PAGES INTEGRATOR */}
            <div className="pt-6 border-t border-white/5 space-y-4 text-left">
              <div>
                <h4 className="text-xs font-black font-sans uppercase tracking-wider text-white">🔗 Public Legal Pages & Web-Embed Links</h4>
                <p className="text-[10px] text-zinc-400 font-mono mt-0.5">Use these public routing paths or direct HTML snippets to link your generated Nexus policies to main landing layouts.</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3 p-4 bg-zinc-950/90 rounded-xl border border-zinc-900">
                  <strong className="text-[9.5px] font-mono uppercase tracking-widest text-[#00ffd2] block">[ Target URL Routes ]</strong>
                  
                  <div className="space-y-2 text-[9.5px] font-mono text-zinc-300">
                    <div className="flex justify-between items-center p-2 rounded bg-black/60 border border-zinc-850">
                      <div>
                        <div className="text-zinc-500 text-[8px] uppercase">Privacy Policy URL</div>
                        <span className="text-white hover:underline cursor-pointer">/privacy-policy</span>
                      </div>
                      <button 
                        onClick={() => {
                          navigator.clipboard.writeText('/privacy-policy');
                          showNoticeLocal("Privacy policy route copied.", "success");
                        }}
                        className="px-2 py-1 bg-zinc-900 border border-zinc-800 hover:bg-zinc-800 text-[8px] uppercase rounded"
                      >
                        Copy
                      </button>
                    </div>

                    <div className="flex justify-between items-center p-2 rounded bg-black/60 border border-zinc-850">
                      <div>
                        <div className="text-zinc-500 text-[8px] uppercase">Cookie Policy URL</div>
                        <span className="text-white hover:underline cursor-pointer">/cookie-policy</span>
                      </div>
                      <button 
                        onClick={() => {
                          navigator.clipboard.writeText('/cookie-policy');
                          showNoticeLocal("Cookie policy route copied.", "success");
                        }}
                        className="px-2 py-1 bg-zinc-900 border border-zinc-c0 hover:bg-zinc-800 text-[8px] uppercase rounded"
                      >
                        Copy
                      </button>
                    </div>

                    <div className="flex justify-between items-center p-2 rounded bg-black/60 border border-zinc-850">
                      <div>
                        <div className="text-zinc-500 text-[8px] uppercase">Terms & Conditions URL</div>
                        <span className="text-white hover:underline cursor-pointer">/terms-conditions</span>
                      </div>
                      <button 
                        onClick={() => {
                          navigator.clipboard.writeText('/terms-conditions');
                          showNoticeLocal("Terms route copied.", "success");
                        }}
                        className="px-2 py-1 bg-zinc-900 border border-zinc-800 hover:bg-zinc-800 text-[8px] uppercase rounded"
                      >
                        Copy
                      </button>
                    </div>

                    <div className="flex justify-between items-center p-2 rounded bg-black/60 border border-zinc-850">
                      <div>
                        <div className="text-zinc-500 text-[8px] uppercase">Refunds & Cancellation URL</div>
                        <span className="text-white hover:underline cursor-pointer">/refunds-cancellations</span>
                      </div>
                      <button 
                        onClick={() => {
                          navigator.clipboard.writeText('/refunds-cancellations');
                          showNoticeLocal("Refund policy route copied.", "success");
                        }}
                        className="px-2 py-1 bg-zinc-900 border border-zinc-800 hover:bg-zinc-800 text-[8px] uppercase rounded"
                      >
                        Copy
                      </button>
                    </div>

                    <div className="flex justify-between items-center p-2 rounded bg-black/60 border border-zinc-850">
                      <div>
                        <div className="text-zinc-500 text-[8px] uppercase">Accessibility statement</div>
                        <span className="text-white hover:underline cursor-pointer">/accessibility</span>
                      </div>
                      <button 
                        onClick={() => {
                          navigator.clipboard.writeText('/accessibility');
                          showNoticeLocal("Accessibility statement route copied.", "success");
                        }}
                        className="px-2 py-1 bg-zinc-900 border border-zinc-800 hover:bg-zinc-800 text-[8px] uppercase rounded"
                      >
                        Copy
                      </button>
                    </div>
                  </div>
                </div>

                {/* HTML SCRIPT FOOTER GENERATOR */}
                <div className="p-4 bg-zinc-950/90 rounded-xl border border-zinc-900 flex flex-col justify-between">
                  <div className="space-y-4">
                    <strong className="text-[9.5px] font-mono uppercase tracking-widest text-[#e840f3] block">[ Consent Banner Embed Code ]</strong>
                    <p className="text-[9.5px] text-zinc-400 leading-relaxed font-mono">
                      Copy this lightweight script to embed the configured for public-preview readiness **Nexus Handshake Consent Banner** sitewide on any server or custom layout:
                    </p>
                    <textarea 
                      readOnly
                      value={`<!-- Nexus Consent Handshake script v1.2 -->\n<script src="https://nexoraxdigital.co.uk/sdk/consent-handshake.js" async data-user-email="siddique.mahmood@gmail.com" data-theme="宇宙" data-banner-id="NEXUS-M-${Math.floor(1000 + Math.random() * 9000)}"></script>`}
                      className="w-full text-[8.5px] font-mono text-[#00ffd2] bg-black border border-zinc-800 rounded p-2.5 h-20 select-all focus:outline-none"
                    />
                  </div>
                  <button 
                    onClick={() => {
                      navigator.clipboard.writeText(`<!-- Nexus Consent Handshake script v1.2 -->\n<script src="https://nexoraxdigital.co.uk/sdk/consent-handshake.js" async data-user-email="siddique.mahmood@gmail.com" data-theme="宇宙"></script>`);
                      showNoticeLocal("NexoraX HTML embed script copied to clipboard.", "success");
                    }}
                    className="w-full mt-3 py-2 bg-gradient-to-r from-purple-950 to-indigo-950 hover:from-purple-900 hover:to-indigo-900 text-white border border-purple-500/30 text-[9.5px] font-bold rounded uppercase cursor-pointer"
                  >
                    📋 Copy HTML script Embed
                  </button>
                </div>
              </div>
            </div>

          </motion.div>
        )}

      </AnimatePresence>

      {/* PUBLIC FOOTER LINKS MATRIX (Strictly non-admin options only) */}
      <div className="pt-6 border-t border-white/10 font-mono text-[10px] text-zinc-500 text-left space-y-4">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-2.5">
          <div className="space-y-1">
            <strong className="text-white uppercase font-sans text-xs">NexoraX NexoraX Public Links</strong>
            <p className="text-[9.5px] text-zinc-500">Natively mapped compliant public paths for cookies setting and user disclosures.</p>
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-2 text-[#00ffd2] font-black">
            <button className="underline cursor-pointer" onClick={() => setCurrentSection('PRIVACY_BUILDER')}>Privacy Policy</button>
            <button className="underline cursor-pointer" onClick={() => setCurrentSection('COOKIE_BUILDER')}>Cookie Policy</button>
            <button className="underline cursor-pointer" onClick={() => setCurrentSection('TERMS')}>Terms & Conditions</button>
            <button className="underline cursor-pointer" onClick={() => setCurrentSection('REFUND_POLICY')}>Refund Policy</button>
            <button className="underline cursor-pointer" onClick={() => setCurrentSection('REFUND_POLICY')}>Cancellation Policy</button>
            <button className="underline cursor-pointer" onClick={() => setCurrentSection('PREFERENCE_CENTER')}>Cookie Settings / Preferences</button>
            <button className="underline cursor-pointer" onClick={() => setCurrentSection('ACCESSIBILITY')}>Accessibility</button>
            <button className="underline cursor-pointer" onClick={() => setCurrentSection('DATA_RIGHTS')}>Privacy Rights Request</button>
          </div>
        </div>
        <p className="text-[8.5px] text-zinc-600">
          © 2026 NexoraX Digital Ltd (SC887095) under direct authority of Director Lead Director. Public legal paths comply with GDPR / UK DPA data minimization framework.
        </p>
      </div>

    </div>
  );
}
